// ============================================================
// FONADHOO CRM - HYBRID VOTER PROFILE
// Project 1 layout + Project 2 features
// ============================================================

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import FamilyTree, { getImmediateFamily } from './FamilyTree';
import AddMemberModal2Step from './AddMemberModal2Step';
import {
  ISSUE_OPTIONS,
  GENDER_OPTIONS,
  OCCUPATION_CATEGORIES,
  OUTREACH_TYPES,
  OUTREACH_TYPE_GROUPS,
  OUTREACH_OUTCOMES,
  SUPPORT_LEVELS,
  hasEffectivePermission
} from './shared/constants';
import { calculateAge } from './shared/utils';
import { uploadPhoto, deletePhoto } from './shared/photoStorage';
import { usePhotoUrl } from './shared/usePhotoUrl';
import SearchableSelect from './shared/SearchableSelect';
import { useBallotBoxes } from './shared/useBallotBoxes';
import { Icon } from './shared/Icon';

// ------------------------------------------------------------
// Fetch ALL voters, paginating past Supabase's 1000-row default cap.
// This is THE fix for family tree members linked to voters beyond the
// first 1000 alphabetically: without this, getV() silently returned null
// for them and they became non-navigable (default cursor, no click).
// ------------------------------------------------------------
async function fetchAllVoters(supabase) {
  const PAGE = 1000;
  let all = [];
  let from = 0;
  // Hard safety cap to avoid infinite loops if something goes wrong.
  for (let i = 0; i < 200; i++) {
    const { data, error } = await supabase
      .from('voters_secured')
      .select('*')
      .order('name')
      .range(from, from + PAGE - 1);
    if (error) {
      console.error('fetchAllVoters error:', error);
      break;
    }
    if (!data || data.length === 0) break;
    all = all.concat(data);
    if (data.length < PAGE) break;
    from += PAGE;
  }
  return all;
}

// Support level colors (Project 1 style)
const SC = {
  'Strong Support':  { bg:'var(--sup-strong-bg)',       text:'var(--sup-strong-text)',       dot:'var(--sup-strong-dot)' },
  'Leaning Support': { bg:'var(--sup-lean-bg)',         text:'var(--sup-lean-text)',         dot:'var(--sup-lean-dot)' },
  'Undecided':       { bg:'var(--sup-undecided-bg)',    text:'var(--sup-undecided-text)',    dot:'var(--sup-undecided-dot)' },
  'Leaning Against': { bg:'var(--sup-against-lean-bg)', text:'var(--sup-against-lean-text)', dot:'var(--sup-against-lean-dot)' },
  'Strong Against':  { bg:'var(--sup-against-bg)',      text:'var(--sup-against-text)',      dot:'var(--sup-against-dot)' },
};

// Party status colors. Values are the short party names (also what's stored
// in voters.party_status). `none` is the default for unspecified. Colors are
// chosen to be distinct in the tree/map where party status appears as a dot.
const PM = {
  MDP:       { label: 'MDP',       color: '#22c55e', bg: '#14532d' },
  PNC:       { label: 'PNC',       color: '#ef4444', bg: '#450a0a' },
  AP:        { label: 'AP',        color: '#a78bfa', bg: '#2e1065' },
  JP:        { label: 'JP',        color: '#eab308', bg: '#422006' },
  MDA:       { label: 'MDA',       color: '#fb923c', bg: '#7c2d12' },
  MLCD:      { label: 'MLCD',      color: '#f0abfc', bg: '#4a044e' },
  MNP:       { label: 'MNP',       color: '#67e8f9', bg: '#0c4a6e' },
  Democrats: { label: 'Democrats', color: '#38bdf8', bg: '#0c4a6e' },
  PNF:       { label: 'PNF',       color: '#f97316', bg: '#431407' },
  none:      { label: 'No Party',  color: 'var(--text-4)', bg: '#0f172a' },
};

// Shared styles (Project 1 style)
const S = {
  inp: {
    padding:'7px 10px',borderRadius:7,border:'1px solid var(--border)',background:'var(--bg)',
    color:'var(--text)',fontSize:11,outline:'none',width:'100%',boxSizing:'border-box'
  },
  sel: {
    padding:'7px 10px',borderRadius:7,border:'1px solid var(--border)',background:'var(--bg)',
    color:'var(--text)',fontSize:11,width:'100%',boxSizing:'border-box',cursor:'pointer'
  },
  minInp: {
    padding:'4px 8px',borderRadius:5,border:'1px solid var(--border)',background:'var(--surface-2)',
    color:'var(--text)',fontSize:10,outline:'none'
  }
};

//Card component (Project 1 style)
const Card = ({children, style}) => (
  <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:12,padding:'12px 14px',boxShadow:'var(--shadow-sm)',...style}}>
    {children}
  </div>
);

// Section title (Project 1 style)
const SecTitle = ({t}) => (
  <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:13,color:'var(--link)',fontWeight:600,marginBottom:8}}>{t}</div>
);

// Property row (Project 1 style)
const PRow = ({label, value}) => (
  <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'4px 0',borderBottom:'1px solid var(--surface-2)'}}>
    <span style={{color:'var(--text-4)',fontSize:10}}>{label}</span>
    <span style={{color:'var(--text)',fontSize:11,fontWeight:500}}>{value}</span>
  </div>
);

// Modal field (Project 1 style)
const MF = ({label, children}) => (
  <div style={{marginBottom:12}}>
    <div style={{fontSize:9,color:'var(--link)',fontWeight:600,marginBottom:5}}>{label}</div>
    {children}
  </div>
);

// Collapsible card wrapper for mobile.
// On desktop (isMobile=false), renders identically to <Card><SecTitle/>...</Card>
//   — no visual or behavioral change whatsoever.
// On mobile (isMobile=true), the section title becomes a tappable header with
//   a chevron that toggles the body. `defaultOpen` controls initial state.
const MobileCollapse = ({title, isMobile, defaultOpen = false, children}) => {
  const [open, setOpen] = useState(defaultOpen);

  if (!isMobile) {
    // Desktop: preserve the original layout exactly
    return (
      <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:12,padding:'12px 14px',boxShadow:'var(--shadow-sm)'}}>
        <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:13,color:'var(--link)',fontWeight:600,marginBottom:8}}>{title}</div>
        {children}
      </div>
    );
  }

  // Mobile: tappable header, collapsible body
  return (
    <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:12,overflow:'hidden',boxShadow:'var(--shadow-sm)'}}>
      <button
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        style={{
          width:'100%',
          display:'flex',
          justifyContent:'space-between',
          alignItems:'center',
          padding:'12px 14px',
          background:'transparent',
          border:'none',
          cursor:'pointer',
          textAlign:'left',
          color:'inherit',
        }}
      >
        <span style={{fontFamily:"'Cormorant Garamond',serif",fontSize:13,color:'var(--link)',fontWeight:600}}>{title}</span>
        <span style={{color:'var(--text-3)', fontSize:14, lineHeight:1, transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition:'transform 0.15s'}}>▾</span>
      </button>
      {open && (
        <div style={{padding:'0 14px 12px 14px'}}>
          {children}
        </div>
      )}
    </div>
  );
};

// ------------------------------------------------------------
// EditMemberPhotoField — photo upload control inside the Edit Member modal.
// Lives here as its own component so it can use hooks (usePhotoUrl) cleanly
// without coupling them to the larger VoterProfile render. Uploads go to
// Storage immediately on file pick; the resolved path lands in
// editingMember.photo_url so the parent's Save handler persists it along
// with everything else.
// ------------------------------------------------------------
function EditMemberPhotoField({ supabase, editingMember, setEditingMember, linkedVoter, refreshVoter }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  // Preview: family-member's own photo wins, fall back to the linked voter's photo.
  const linkedVoterPhoto = linkedVoter?.photo_url || null;
  const effectivePhoto = editingMember.photo_url || linkedVoterPhoto || null;
  const previewUrl = usePhotoUrl(supabase, effectivePhoto);
  const isOverride = !!editingMember.photo_url; // true when a family-member-specific photo exists
  const showingVoterFallback = !editingMember.photo_url && !!linkedVoterPhoto;
  const hasAnyPhoto = !!effectivePhoto;
  
  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setError('');
    setUploading(true);
    try {
      const path = await uploadPhoto(supabase, file, 'family_members', editingMember.id);
      setEditingMember({ ...editingMember, photo_url: path });
    } catch (err) {
      console.error('Member photo upload failed:', err);
      setError(err.message || 'Upload failed');
    } finally {
      setUploading(false);
      if (e.target) e.target.value = '';
    }
  };
  
  const handleRemove = async () => {
    if (editingMember.photo_url) {
      // Remove the family-member override only. Falls back to voter's photo in tree.
      deletePhoto(supabase, editingMember.photo_url);
      setEditingMember({ ...editingMember, photo_url: null });
      return;
    }
    // No override — user wants to remove the voter's profile photo itself.
    if (!linkedVoter || !linkedVoter.photo_url) return;
    if (!window.confirm("This will remove the voter's profile photo. Continue?")) return;
    deletePhoto(supabase, linkedVoter.photo_url);
    await supabase.from('voters').update({ photo_url: null }).eq('id', linkedVoter.id);
    if (refreshVoter) await refreshVoter();
  };
  
  const initials = (editingMember.name || '?')
    .split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
  
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{
          width: 56, height: 56, borderRadius: '50%',
          border: '1px solid var(--border)',
          background: 'var(--surface-2)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: 'var(--text-4)', fontSize: 18, fontFamily: "'Cormorant Garamond', serif",
          flexShrink: 0,
          opacity: uploading ? 0.5 : 1,
          overflow: 'hidden'
        }}>
          {previewUrl ? (
            <img
              src={previewUrl}
              alt=""
              style={{
                width: '100%', height: '100%',
                objectFit: 'cover', objectPosition: 'center',
                display: 'block'
              }}
            />
          ) : (
            initials
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <label style={{
            padding: '5px 11px',
            background: uploading ? 'var(--surface-2)' : 'var(--border)',
            border: '1px solid var(--border)', borderRadius: 5,
            color: uploading ? 'var(--text-4)' : 'var(--link)',
            cursor: uploading ? 'wait' : 'pointer',
            fontSize: 11, fontFamily: 'DM Mono, monospace', textAlign: 'center'
          }}>
            {uploading
              ? 'Uploading…'
              : (isOverride
                ? 'Change photo'
                : (showingVoterFallback ? 'Override with new photo' : 'Upload photo'))}
            <input
              type="file"
              accept="image/*"
              disabled={uploading}
              onChange={handleFile}
              style={{ display: 'none' }}
            />
          </label>
          {hasAnyPhoto && !uploading && (
            <button
              type="button"
              onClick={handleRemove}
              title={isOverride
                ? (linkedVoterPhoto ? "Remove this override (will show voter's profile photo)" : 'Remove photo')
                : "Remove voter's profile photo"}
              style={{
                padding: '5px 11px', background: 'transparent',
                border: '1px solid rgba(239,68,68,0.5)', borderRadius: 5,
                color: '#ef4444', cursor: 'pointer', fontSize: 11,
                fontFamily: 'DM Mono, monospace'
              }}>
              Remove
            </button>
          )}
        </div>
      </div>
      {error && (
        <div style={{ fontSize: 10, color: '#ef4444', marginTop: 6 }}>
          {error}
        </div>
      )}
    </div>
  );
}

// ── Outreach Log Form ──────────────────────────────────────
function OutreachLogForm({
  onSave,
  typeGroups = OUTREACH_TYPE_GROUPS, customTypes = [],
  outcomes = OUTREACH_OUTCOMES, customOutcomes = [],
  canManage = false, onAddType, onDeleteType, onAddOutcome, onDeleteOutcome,
}) {
  const [type, setType] = useState(null);
  const [outcome, setOutcome] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [addingType, setAddingType] = useState(false);
  const [newType, setNewType] = useState('');
  const [addingOutcome, setAddingOutcome] = useState(false);
  const [newOutcome, setNewOutcome] = useState('');

  const builtInTypes = typeGroups.flatMap(g => g.types);

  const chipStyle = (on) => ({
    padding: '5px 11px', borderRadius: 8,
    border: on ? '1px solid #3b82f6' : '1px solid var(--border)',
    background: on ? 'rgba(59,130,246,0.13)' : 'var(--surface-2)',
    color: on ? '#3b82f6' : 'var(--text-2)',
    fontSize: 11, fontWeight: on ? 600 : 500,
    fontFamily: "'DM Mono', monospace", transition: 'all 0.1s',
  });

  // Generic chip. `onDelete` (custom items only) adds a × inside the chip.
  const renderChip = (label, on, onSelect, onDelete) => onDelete ? (
    <span key={label} style={{ ...chipStyle(on), display: 'inline-flex', alignItems: 'center', gap: 7, paddingRight: 7 }}>
      <span onClick={onSelect} style={{ cursor: 'pointer' }}>{label}</span>
      <span role="button" title="Delete" onClick={onDelete}
        style={{ cursor: 'pointer', color: on ? '#3b82f6' : 'var(--text-4)', fontSize: 14, lineHeight: 1, opacity: 0.75 }}>×</span>
    </span>
  ) : (
    <button key={label} onClick={onSelect} style={{ ...chipStyle(on), cursor: 'pointer' }}>{label}</button>
  );

  // Generic inline "+ Add …" control.
  const renderAdd = (adding, setAdding, val, setVal, onAdd, placeholder, addLabel) => adding ? (
    <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
      <input value={val} onChange={e => setVal(e.target.value)} autoFocus placeholder={placeholder}
        onKeyDown={e => { if (e.key === 'Enter') onAdd(); if (e.key === 'Escape') { setAdding(false); setVal(''); } }}
        style={{ padding: '5px 10px', borderRadius: 7, border: '1px solid var(--border)', background: 'var(--surface-2)', color: 'var(--text)', fontSize: 11, fontFamily: "'DM Mono', monospace", outline: 'none', minWidth: 150 }} />
      <button onClick={onAdd} disabled={!val.trim()} style={{ padding: '5px 12px', borderRadius: 7, border: 'none', background: val.trim() ? '#3b82f6' : 'var(--surface-2)', color: val.trim() ? '#fff' : 'var(--text-4)', fontSize: 11, fontWeight: 600, cursor: val.trim() ? 'pointer' : 'default' }}>Add</button>
      <button onClick={() => { setAdding(false); setVal(''); }} style={{ padding: '5px 9px', borderRadius: 7, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-3)', fontSize: 11, cursor: 'pointer' }}>Cancel</button>
    </span>
  ) : (
    <button onClick={() => setAdding(true)} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 11px', borderRadius: 8, border: '1px dashed var(--border)', background: 'transparent', color: 'var(--text-3)', fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: "'DM Mono', monospace" }}><Icon name="plus" size={12} /> {addLabel}</button>
  );

  const handleAddType = async () => { const ok = onAddType && await onAddType(newType.trim()); if (ok) { setType(newType.trim()); setNewType(''); setAddingType(false); } };
  const handleAddOutcome = async () => { const ok = onAddOutcome && await onAddOutcome(newOutcome.trim()); if (ok) { setOutcome(newOutcome.trim()); setNewOutcome(''); setAddingOutcome(false); } };

  const handleSave = async () => {
    if (!type) return;
    setSaving(true);
    try { await onSave(type, outcome || null, notes || null); setType(null); setNotes(''); setOutcome(''); }
    catch (e) { console.error('[OutreachLogForm] save failed:', e); }
    setSaving(false);
  };

  const fieldLabel = { fontFamily: "'DM Mono', monospace", fontSize: 9, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--text-4)', marginBottom: 6 };

  return (
    <div style={{ marginBottom: 10 }}>
      {/* Type */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
        {builtInTypes.map(t => renderChip(t, type === t, () => setType(t)))}
        {customTypes.map(t => renderChip(t, type === t, () => setType(t), canManage ? () => onDeleteType(t) : null))}
        {canManage && renderAdd(addingType, setAddingType, newType, setNewType, handleAddType, 'New outreach type…', 'Add type')}
      </div>

      {/* Outcome */}
      <div style={fieldLabel}>Outcome (optional)</div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
        {outcomes.map(o => renderChip(o, outcome === o, () => setOutcome(outcome === o ? '' : o)))}
        {customOutcomes.map(o => renderChip(o, outcome === o, () => setOutcome(outcome === o ? '' : o), canManage ? () => onDeleteOutcome(o) : null))}
        {canManage && renderAdd(addingOutcome, setAddingOutcome, newOutcome, setNewOutcome, handleAddOutcome, 'New outcome…', 'Add outcome')}
      </div>

      <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Notes (optional)..."
        style={{ width: '100%', padding: '6px 9px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text-2)', fontSize: 11, resize: 'vertical', minHeight: 48, marginBottom: 6, boxSizing: 'border-box' }} />
      <button onClick={handleSave} disabled={saving || !type} style={{ width: '100%', padding: '7px 0', borderRadius: 6, border: 'none', background: !type ? 'var(--surface-2)' : saving ? 'var(--border)' : '#3b82f6', color: !type ? 'var(--text-4)' : saving ? 'var(--link)' : '#fff', cursor: (saving || !type) ? 'default' : 'pointer', fontSize: 12, fontWeight: 600 }}>
        {saving ? 'Saving…' : !type ? 'Select a type above' : '+ Log Outreach'}
      </button>
    </div>
  );
}

function VoterProfile({voter, supabase, profile, onClose, allLocations = [], opponentNames = [], initialAllVoters = [], initialFamilies = [], refreshFamilies}) {
  const navigate = useNavigate();
  const [form, setForm] = useState({...voter});
  const [editing, setEditing] = useState(false);
  const [editingVotes, setEditingVotes] = useState(false);
  const [headerEl, setHeaderEl] = useState(null);
  useEffect(() => {
    setHeaderEl(document.querySelector('[data-main-header]'));
  }, []);
  const [outreach, setOutreach] = useState([]);
  // Custom outreach types (added by super_admin / campaign_manager), stored in
  // app_settings('outreach_types') so they're shared across the team.
  const [customOutreachTypes, setCustomOutreachTypes] = useState([]);
  const [customOutreachOutcomes, setCustomOutreachOutcomes] = useState([]);
  const canManageOutreachTypes = profile?.role === 'super_admin' || profile?.role === 'campaign_manager';
  const [supportHistory, setSupportHistory] = useState([]);
  // Multiple notes per voter, stored in voter_notes table (mirrors outreach).
  // `noteDraft` is the new-note input box; cleared after a successful save.
  const [voterNotes, setVoterNotes] = useState([]);
  const [noteDraft, setNoteDraft] = useState('');
  const [noteSaving, setNoteSaving] = useState(false);
  // "Show all" toggles: by default we only show the latest 1 outreach
  // entry and the latest 1 note. The user clicks "Show all (N)" to reveal
  // the rest. Keeps the profile page short on busy voters.
  const [showAllOutreach, setShowAllOutreach] = useState(false);
  const [showAllNotes, setShowAllNotes] = useState(false);
  const [showAddMember, setShowAddMember] = useState(false);
  const [showEditMember, setShowEditMember] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  // Voter linking state for Edit modal
  const [editVoterSearch, setEditVoterSearch] = useState('');
  const [editVoterResults, setEditVoterResults] = useState([]);
  const [editVoterSearching, setEditVoterSearching] = useState(false);
  // When true, the Linked Voter section shows a search UI alongside the
  // current link so the user can pick a different voter without losing
  // context. Cancelling restores the current link; picking a new voter
  // stages it into editingMember.voter_id (committed on Save Links).
  const [changingVoter, setChangingVoter] = useState(false);
  const [localVoters, setLocalVoters] = useState(initialAllVoters);
  // Sync local cache when App's allVoters finishes loading (or refreshes).
  // Without this, the tree first paints during the loading window with no
  // photos / support dots / clickable voters, and never recovers because
  // useState only takes its initial value once.
  useEffect(() => { setLocalVoters(initialAllVoters); }, [initialAllVoters]);
  const [families, setFamilies] = useState(initialFamilies);
  // Keep local families in sync with the parent's copy — parent refetches
  // after mutations and on session load.
  useEffect(() => { setFamilies(initialFamilies); }, [initialFamilies]);
  const [manageMembersExpanded, setManageMembersExpanded] = useState(false);
  const fileRef = useRef();
  
  // Notes auto-save: debounce the DB write so every keystroke doesn't round
  // trip. Status is 'idle' | 'saving' | 'saved' | 'error'; the chip at the
  // corner of the Notes card shows it.
  const notesSaveTimer = useRef(null);
  const [notesStatus, setNotesStatus] = useState('idle');
  
  // Ballot boxes — fetched live from Supabase. Admins can add new entries
  // inline from the box dropdown; everyone else just picks from the list.
  const { boxes: ballotBoxes, addBox: addBallotBox } = useBallotBoxes(supabase);
  const canAddBallotBox = hasEffectivePermission(profile, 'canManageUsers');

  // isAssignedToMe: original logic used by canEdit — untouched.
  const isAssignedToMe = !!(voter?.gotv_assigned_to && voter.gotv_assigned_to === profile?.id);
  const isInTerritory = !!(profile?.assigned_territory && voter
    && (voter.island === profile.assigned_territory || voter.ward === profile.assigned_territory));
  // inScope: uses full_name match so GOTV-assigned users can see/edit support.
  const isGOTVAssigned = !!(voter?.gotv_assigned_to && profile?.full_name
    && voter.gotv_assigned_to === profile.full_name);
  const inScope = isGOTVAssigned || isInTerritory;

  const canEdit      = hasEffectivePermission(profile, 'canEditInfo')
                     && (hasEffectivePermission(profile, 'canViewAll') || inScope || isAssignedToMe);
  const isPatchAgent  = profile?.role === 'patch_agent';
  const canSeeVotes  = hasEffectivePermission(profile, 'canViewVotes') || (inScope && isPatchAgent);
  const canEditVotes_ = hasEffectivePermission(profile, 'canEditVotes') || (inScope && isPatchAgent) || canEdit;

  // Responsive breakpoint: stack the two-column layout into one on narrow
  // screens so the page doesn't scroll sideways on phones. 768px matches
  // the Family Tree modal's breakpoint for consistency across the app.
  //
  // IMPORTANT: We use matchMedia instead of window.innerWidth because on
  // iOS Safari, the auto-hiding URL bar fires resize events on scroll,
  // and the reported innerWidth can momentarily flip — causing the layout
  // to bounce between mobile and desktop mid-scroll. matchMedia is tied
  // to the actual CSS media query state, so it only changes when the
  // breakpoint is truly crossed (e.g. rotating the device or resizing
  // the window), not on scroll.
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia('(max-width: 767px)').matches;
  });
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(max-width: 767px)');
    const onChange = (e) => setIsMobile(e.matches);
    // Some older Safari versions use addListener/removeListener instead
    // of addEventListener; support both.
    if (mq.addEventListener) {
      mq.addEventListener('change', onChange);
    } else if (mq.addListener) {
      mq.addListener(onChange);
    }
    return () => {
      if (mq.removeEventListener) {
        mq.removeEventListener('change', onChange);
      } else if (mq.removeListener) {
        mq.removeListener(onChange);
      }
    };
  }, []);

  // Load voter data, outreach, and families
  useEffect(() => {
    // Reload voter data
    supabase.from('voters_secured').select('*').eq('id', voter.id).single()
      .then(({data, error}) => {
        if (error) console.error('[voter] reload failed:', error);
        if (data) {
          setForm(data);
        }
      });

    // Load outreach history
    supabase.from('outreach').select('*').eq('voter_id', voter.id).order('date', {ascending: false})
      .then(({data, error}) => {
        if (error) console.error('[outreach] load failed:', error);
        setOutreach(data || []);
      });

    // Load support change history (leaders only — query is cheap, filtering done in render)
    supabase.from('support_history')
      .select('changed_from, changed_to, changed_at, editor:profiles!changed_by(full_name)')
      .eq('voter_id', voter.id)
      .order('changed_at', { ascending: false })
      .then(({data, error}) => {
        if (error) console.error('[support_history] load failed:', error);
        setSupportHistory(data || []);
      });

    // Load voter notes (multiple, newest first). Mirrors outreach loading.
    supabase.from('voter_notes').select('*').eq('voter_id', voter.id).order('created_at', {ascending: false})
      .then(({data, error}) => {
        if (error) {
          console.error('[voter_notes] load failed:', error);
          // Non-fatal — notes table may not exist yet on older deployments.
          // The legacy single-textarea field remains in `form.notes` as a
          // fallback display below.
          setVoterNotes([]);
          return;
        }
        setVoterNotes(data || []);
      });
    
    // localVoters initialized from initialAllVoters prop (App.jsx already loaded them).
    // Only re-fetch on demand (after photo upload below) — no eager full reload here.

    // families come from the parent via initialFamilies prop — loaded once at
    // session start. No per-voter refetch, which was the main cause of slow
    // family-tree navigation. Local mutations (Add/Edit/Delete Member) call
    // refreshFamilies() to bubble the change back up.
  }, [voter.id, supabase]);

  // Load any team-defined custom outreach types + outcomes once.
  useEffect(() => {
    supabase.from('app_settings').select('key, value').in('key', ['outreach_types', 'outreach_outcomes'])
      .then(({ data, error }) => {
        if (error) console.error('[app_settings] outreach types/outcomes load failed:', error);
        for (const row of (data || [])) {
          if (!Array.isArray(row.value)) continue;
          if (row.key === 'outreach_types') setCustomOutreachTypes(row.value.filter(Boolean));
          if (row.key === 'outreach_outcomes') setCustomOutreachOutcomes(row.value.filter(Boolean));
        }
      });
  }, [supabase]);

  // Voter search for Edit Member modal (debounced via Supabase ilike)
  useEffect(() => {
    if (!showEditMember) return;
    if (!editVoterSearch.trim()) {
      setEditVoterResults([]);
      return;
    }
    setEditVoterSearching(true);
    supabase
      .from('voters')
      .select('id,name,island,ward')
      .ilike('name', `%${editVoterSearch.trim()}%`)
      .order('name')
      .limit(100)
      .then(({ data, error }) => {
        if (!error) setEditVoterResults(data || []);
        setEditVoterSearching(false);
      });
  }, [editVoterSearch, showEditMember, supabase]);

  const up = (f, v) => setForm(p => ({...p, [f]: v}));
  
  // If a notes save is still debouncing when the component unmounts OR the
  // viewed voter changes, FLUSH the save (fire it now) rather than drop it.
  // Dropping would lose the user's last keystrokes before they navigated.
  // Uses form.notes at flush time — closures capture the right value via
  // the effect's own scope.
  const pendingNotesRef = useRef(null);
  pendingNotesRef.current = form.notes;
  useEffect(() => {
    const savedVoterId = voter.id;
    return () => {
      if (notesSaveTimer.current) {
        clearTimeout(notesSaveTimer.current);
        notesSaveTimer.current = null;
        // Fire a blind save against the voter we were just viewing, using
        // the last-known notes value. We don't await — the component is
        // unmounting; the write completes in the background.
        supabase
          .from('voters')
          .update({ notes: pendingNotesRef.current ?? '' })
          .eq('id', savedVoterId)
          .then(({ error }) => {
            if (error) console.error('[notes] flush on unmount failed:', error);
          });
      }
    };
  }, [voter.id, supabase]);

  // Photo upload state for the voter avatar.
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoError, setPhotoError] = useState('');

  const handlePhoto = useCallback(async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPhotoError('');
    setPhotoUploading(true);
    try {
      const path = await uploadPhoto(supabase, file, 'voters', voter.id);
      // Persist immediately so the change isn't lost if the user navigates
      // away without hitting the Save button. Voter photo updates are a
      // single-field op so this is safe.
      const { error } = await supabase
        .from('voters')
        .update({ photo_url: path })
        .eq('id', voter.id);
      if (error) throw new Error(error.message);
      up('photo_url', path);
    } catch (err) {
      console.error('Voter photo upload failed:', err);
      setPhotoError(err.message || 'Upload failed');
    } finally {
      setPhotoUploading(false);
      // Reset the input so the same file can be re-selected if needed.
      if (e.target) e.target.value = '';
    }
  }, [supabase, voter.id]);

  // Resolve the voter's stored photo_url (path | base64 | null) to a URL
  // we can put in `background: url(...)`. Re-runs when the field changes.
  const voterPhotoUrl = usePhotoUrl(supabase, form.photo_url);

  const addOut = async (type, outcome, notes) => {
    const {data, error} = await supabase.from('outreach').insert({
      voter_id: voter.id,
      type,
      outcome: outcome || null,
      notes: notes || null,
      date: new Date().toISOString().split('T')[0],
      logged_by: profile.id
    }).select().single();
    if (error) { console.error('[addOut] failed:', error); return; }
    if (data) setOutreach(o => [data, ...o]);
    else {
      // fallback: reload outreach list
      const {data: all} = await supabase.from('outreach').select('*').eq('voter_id', voter.id).order('date', {ascending: false});
      if (all) setOutreach(all);
    }
    // Commitment Made auto-upgrades the voter's support level to Strong Support
    if (outcome === 'Commitment Made' && voter.support !== 'Strong Support') {
      await supabase.from('voters').update({ support: 'Strong Support' }).eq('id', voter.id);
      setForm(f => ({ ...f, support: 'Strong Support' }));
      Object.assign(voter, { support: 'Strong Support' });
    }
  };

  const rmOut = async (id) => {
    await supabase.from('outreach').delete().eq('id', id);
    setOutreach(o => o.filter(x => x.id !== id));
  };

  // Persist a new custom outreach type (gated to super_admin / campaign_manager
  // both in the UI and by app_settings RLS). Returns true on success.
  const addOutreachType = async (name) => {
    const clean = (name || '').trim();
    if (!clean) return false;
    const exists = [...OUTREACH_TYPES, ...customOutreachTypes].some(t => t.toLowerCase() === clean.toLowerCase());
    if (exists) { alert('That outreach type already exists.'); return false; }
    const next = [...customOutreachTypes, clean];
    const { error } = await supabase.from('app_settings').upsert({ key: 'outreach_types', value: next });
    if (error) { alert('Could not add type: ' + error.message); return false; }
    setCustomOutreachTypes(next);
    return true;
  };

  // Remove a custom outreach type. Existing outreach rows keep their stored
  // label — this only drops the option from the picker.
  const deleteOutreachType = async (name) => {
    if (!window.confirm(`Delete custom type "${name}"? Existing records keep their label.`)) return;
    const next = customOutreachTypes.filter(t => t !== name);
    const { error } = await supabase.from('app_settings').upsert({ key: 'outreach_types', value: next });
    if (error) { alert('Could not delete type: ' + error.message); return; }
    setCustomOutreachTypes(next);
  };

  // Custom outreach outcomes — same pattern as types (app_settings key 'outreach_outcomes').
  const addOutreachOutcome = async (name) => {
    const clean = (name || '').trim();
    if (!clean) return false;
    const exists = [...OUTREACH_OUTCOMES, ...customOutreachOutcomes].some(o => o.toLowerCase() === clean.toLowerCase());
    if (exists) { alert('That outcome already exists.'); return false; }
    const next = [...customOutreachOutcomes, clean];
    const { error } = await supabase.from('app_settings').upsert({ key: 'outreach_outcomes', value: next });
    if (error) { alert('Could not add outcome: ' + error.message); return false; }
    setCustomOutreachOutcomes(next);
    return true;
  };
  const deleteOutreachOutcome = async (name) => {
    if (!window.confirm(`Delete custom outcome "${name}"? Existing records keep their label.`)) return;
    const next = customOutreachOutcomes.filter(o => o !== name);
    const { error } = await supabase.from('app_settings').upsert({ key: 'outreach_outcomes', value: next });
    if (error) { alert('Could not delete outcome: ' + error.message); return; }
    setCustomOutreachOutcomes(next);
  };

  // Add a new note to voter_notes. Mirrors addOut: optimistic insert with
  // a fallback reload if Supabase doesn't return the inserted row.
  const addNote = async () => {
    const text = (noteDraft || '').trim();
    if (!text) return;
    setNoteSaving(true);
    const {data, error} = await supabase.from('voter_notes').insert({
      voter_id: voter.id,
      note: text,
      created_by: profile.id,
    }).select().single();
    setNoteSaving(false);
    if (error) {
      console.error('[addNote] failed:', error);
      return;
    }
    if (data) setVoterNotes(n => [data, ...n]);
    else {
      const {data: all} = await supabase.from('voter_notes').select('*').eq('voter_id', voter.id).order('created_at', {ascending: false});
      if (all) setVoterNotes(all);
    }
    setNoteDraft('');
  };

  const rmNote = async (id) => {
    await supabase.from('voter_notes').delete().eq('id', id);
    setVoterNotes(n => n.filter(x => x.id !== id));
  };

  // Notes: immediate persist. Called by the debounced timer AND by onBlur,
  // so whichever fires first wins. Safe to call with any value — the
  // optimistic update already happened via up('notes', ...).
  const saveNotesNow = async (value) => {
    setNotesStatus('saving');
    const { error } = await supabase
      .from('voters')
      .update({ notes: value ?? '' })
      .eq('id', voter.id);
    if (error) {
      console.error('[notes] save failed:', error);
      setNotesStatus('error');
    } else {
      setNotesStatus('saved');
      // Revert the "saved" chip to idle after a moment so it doesn't
      // linger on screen forever.
      setTimeout(() => setNotesStatus(s => s === 'saved' ? 'idle' : s), 1500);
    }
  };

  const saveVoter = async () => {
    // Build update object based on role permissions
    let update = {
      name: form.name,
      house: form.house,
      phone: form.phone,
      phone2: form.phone2 || null,
      phone3: form.phone3 || null,
      atoll: form.atoll || null,
      island: form.island,
      ward: form.ward,
      notes: form.notes,
      influencer: form.influencer,
      social_facebook: form.social_facebook,
      social_instagram: form.social_instagram,
      social_tiktok: form.social_tiktok,
      social_twitter: form.social_twitter,
      family_id: form.family_id,
      photo_url: form.photo_url,
      current_location: form.current_location,
      ballot_box_r1: form.ballot_box_r1 || null,
      ballot_box_r2: form.ballot_box_r2 || null,
      job: form.job,
      // Project 2 additional fields
      gender: form.gender,
      occupation_category: form.occupation_category,
      email: form.email,
      whatsapp: form.whatsapp,
      emergency_contact: form.emergency_contact,
      emergency_phone: form.emergency_phone,
      registration_date: form.registration_date,
      last_contact: form.last_contact,
      contact_method: form.contact_method,
      support_strength: form.support_strength,
      likelihood_to_vote: form.likelihood_to_vote,
      influence_level: form.influence_level,
      vote_commitment: form.vote_commitment,
      commitment_type: form.commitment_type,
      commitment_confidence: form.commitment_confidence,
      voting_history: form.voting_history,
      attended_events: form.attended_events,
      volunteer_interest: form.volunteer_interest,
      donation_potential: form.donation_potential,
      concerns: form.concerns,
      requests: form.requests,
      referral_source: form.referral_source,
      tags: form.tags
    };

    if (canEdit) {
      Object.assign(update, {issues: form.issues || []});
    }
    
    if (canEditVotes_ || editingVotes) {
      const isAgainst = form.support === 'Leaning Against' || form.support === 'Strong Against';
      Object.assign(update, {
        support: form.support,
        preferred_candidate: isAgainst ? (form.preferred_candidate || '') : '',
      });
    }
    
    if (hasEffectivePermission(profile, 'canEditParty')) {
      Object.assign(update, {
        party_status: form.party_status
      });
    }

    await supabase.from('voters').update(update).eq('id', voter.id);

    // Refresh support history & check for commitment withdrawal.
    // The DB trigger has just written a support_history row with reliable from/to values.
    if ('support' in update) {
      const { data: history, error: historyErr } = await supabase.from('support_history')
        .select('changed_from, changed_to, changed_at, editor:profiles!changed_by(full_name)')
        .eq('voter_id', voter.id)
        .order('changed_at', { ascending: false });
      if (historyErr) console.error('[support_history] refresh failed:', historyErr);
      setSupportHistory(history || []);

      const SUPPORTER_LEVELS = ['Strong Support', 'Leaning Support'];
      const latest = history?.[0];
      const wasSupporter = SUPPORTER_LEVELS.includes(latest?.changed_from);
      const isSupporter  = SUPPORTER_LEVELS.includes(latest?.changed_to);
      // If this save transitioned from supporter to non-supporter, delete the
      // voter's most recent Commitment Made outreach so they no longer count as
      // committed. The audit trail lives in the support_history table (already
      // displayed as "Support History" in this profile) — we don't pollute
      // outreach with synthetic records.
      if (latest && wasSupporter && !isSupporter) {
        const { data: commitmentLog } = await supabase.from('outreach')
          .select('id, outcome, created_at')
          .eq('voter_id', voter.id)
          .eq('outcome', 'Commitment Made')
          .order('created_at', { ascending: false })
          .limit(1);
        if (commitmentLog?.[0]) {
          const id = commitmentLog[0].id;
          await supabase.from('outreach').delete().eq('id', id);
          setOutreach(o => o.filter(x => x.id !== id));
        }
      }
    }

    // Update local form state and voter state to trigger re-render
    const updatedVoter = {...voter, ...update};
    setForm(updatedVoter);
    setEditing(false);
    setEditingVotes(false);
    
    // Patch localVoters in memory if photo changed — no need for a full re-fetch
    if (form.photo_url !== voter.photo_url) {
      setLocalVoters(prev => prev.map(v => v.id === voter.id ? { ...v, photo_url: form.photo_url } : v));
      Object.assign(voter, { photo_url: form.photo_url });
    }
  };

  const myFamily = families.find(f => f.id === form.family_id);
  
  // ------------------------------------------------------------
  // VIEWER-RELATIVE FAMILY TREE
  //
  // Problem: a married-in spouse (e.g. Azeeza, linked into Abbas's family
  // via family_id) should see HER OWN family tree when viewing her profile,
  // not her in-laws. Option B from the design: don't change the schema,
  // just compute the tree dynamically from the viewer's bloodline.
  //
  // Algorithm: flatten every family_member across all families into one
  // pool, find the viewer's member record, then breadth-first expand through
  // parent_ids / children / siblings / direct spouses — but NEVER expand
  // a second hop through a spouse's own family. This cleanly stops at
  // in-laws so Azeeza's view doesn't leak into Abbas's parents/siblings.
  // ------------------------------------------------------------
  const treeFamily = (() => {
    // Flatten all members across all families (handles the case where the
    // viewer's own blood relatives live in a different family_id than the
    // one they're currently linked into as a spouse).
    const allMembersFlat = [];
    const seenIds = new Set();
    families.forEach(f => {
      (f.members || []).forEach(m => {
        if (seenIds.has(m.id)) return;
        seenIds.add(m.id);
        allMembersFlat.push(m);
      });
    });
    
    // Find ALL the viewer's family_members rows (matched by voter_id). The
    // viewer can legitimately have multiple rows: one in their in-laws'
    // family (as a spouse) and another in their own new family (as the
    // root of their own blood tree). Both carry relationships we need,
    // so treat them as aliases of the same person.
    const viewerRows = allMembersFlat.filter(m => m.voter_id === voter.id);
    if (viewerRows.length === 0) {
      // Viewer has no family_members row yet. Fall back to the classic
      // family_id lookup so the UI keeps working and so "+ Add Member"
      // can bootstrap a family for them.
      return myFamily || null;
    }
    
    // ALIAS-AWARE GRAPH: any voter-linked member can have MULTIPLE
    // family_members rows across different families, each carrying
    // different relationships. Build a lookup: given any single alias id,
    // get the set of ALL alias ids that represent the same person.
    //
    // Without this, a sibling (like Gamariyyath, whose spouse lives in her
    // in-laws' family) looks like a lone node from the viewer's side — the
    // walker can't find her spouse or children because the row it's
    // holding has spouse_id=null / no children pointing at it. The spouse
    // and children reference her OTHER alias id in the in-laws' family.
    const aliasIdsFor = (() => {
      const byVoterId = new Map();
      allMembersFlat.forEach(m => {
        if (!m.voter_id) return;
        if (!byVoterId.has(m.voter_id)) byVoterId.set(m.voter_id, new Set());
        byVoterId.get(m.voter_id).add(m.id);
      });
      return (id) => {
        const mem = allMembersFlat.find(x => x.id === id);
        if (!mem || !mem.voter_id) return new Set([id]);
        return byVoterId.get(mem.voter_id) || new Set([id]);
      };
    })();
    
    // Build a set of "all ids that represent the viewer". Any `parent_ids`
    // in the data that points at ANY of these ids should be treated as
    // pointing at the viewer. Same for spouse_id.
    const viewerIdSet = aliasIdsFor(viewerRows[0].id);
    
    // Pick a primary viewer row. Order: prefer the row whose family_id is
    // the viewer's currently-linked family (more likely to have the most
    // up-to-date relationships), fall back to the first row.
    const viewerMember =
      viewerRows.find(r => r.family_id === form.family_id) || viewerRows[0];
    
    // Helper: bidirectional spouse lookup, alias-aware on BOTH sides.
    // Returns any row that points at ANY alias of `mem`, or any row that
    // ANY alias of `mem` points at. Handles one-directional spouse_id too.
    const spouseOf = (mem) => {
      if (!mem) return null;
      const memAliases = aliasIdsFor(mem.id);
      // Direction 1: any of mem's aliases has spouse_id set → find that row.
      for (const aliasId of memAliases) {
        const alias = allMembersFlat.find(x => x.id === aliasId);
        if (alias && alias.spouse_id) {
          const direct = allMembersFlat.find(x => x.id === alias.spouse_id);
          if (direct) return direct;
        }
      }
      // Direction 2: any row whose spouse_id points at any of mem's aliases.
      return allMembersFlat.find(x => x.spouse_id && memAliases.has(x.spouse_id)) || null;
    };
    
    // Helper: collect the union of parent_ids across every alias row.
    // Alias-aware for ALL members (not just the viewer) — a sibling's
    // parent_ids might only be recorded on their other alias row.
    const unionParentIds = (id) => {
      const aliases = aliasIdsFor(id);
      const all = new Set();
      aliases.forEach(aid => {
        const row = allMembersFlat.find(x => x.id === aid);
        if (row) (row.parent_ids || []).forEach(p => all.add(p));
      });
      return [...all];
    };
    
    // Helper: find all children of `id` — any member whose parent_ids
    // contains ANY of `id`'s aliases. So a person's children show up
    // regardless of which alias of theirs the children reference.
    const childrenOf = (id) => {
      const targetIds = aliasIdsFor(id);
      return allMembersFlat.filter(child =>
        (child.parent_ids || []).some(pid => targetIds.has(pid))
      );
    };
    
    // The walk has two phases that must NOT mix, or we'll cross through
    // a spouse into their family (the original bug).
    //
    // Phase 1: walk UP from the viewer to collect every ancestor.
    // Phase 2: from every ancestor (including viewer), walk DOWN through
    //          children recursively. A child's `parent_ids` is NOT used
    //          to walk back up — doing so would pull in the other parent
    //          (an in-law) and through them their whole family.
    //
    // The result is the viewer's true bloodline: ancestors + everyone
    // descended from any ancestor (i.e., siblings, cousins, descendants).
    const blood = new Set();
    
    // --- Phase 1: ancestors ---
    const walkUp = (id) => {
      if (blood.has(id)) return;
      blood.add(id);
      unionParentIds(id).forEach(pid => walkUp(pid));
    };
    walkUp(viewerMember.id);
    
    // Snapshot of ancestors (including viewer) — these are the roots
    // from which we walk down.
    const ancestors = [...blood];
    
    // --- Phase 2: descendants of every ancestor ---
    const walkDown = (id) => {
      childrenOf(id).forEach(child => {
        if (blood.has(child.id)) return;
        blood.add(child.id);
        walkDown(child.id);
      });
    };
    ancestors.forEach(id => walkDown(id));
    
    // --- Phase 3: direct spouses of blood members ---
    // These appear in the tree (so couples render as couples, and the
    // viewer's own spouse is visible) but are NOT expanded further.
    const spousesShown = new Set();
    Array.from(blood).forEach(id => {
      const mem = allMembersFlat.find(m => m.id === id);
      const sp = spouseOf(mem);
      if (sp && !blood.has(sp.id)) {
        spousesShown.add(sp.id);
      }
    });
    // Also look for any row that points at ANY viewer-alias as its spouse.
    // This catches the Abbas-points-at-Azeeza case when the walk seed was
    // a different Azeeza row.
    allMembersFlat.forEach(m => {
      if (m.spouse_id && viewerIdSet.has(m.spouse_id)) {
        if (!blood.has(m.id)) spousesShown.add(m.id);
      }
    });
    
    // Deduplicate: collapse all viewer-alias rows into just the primary
    // one, so the tree doesn't render the viewer twice.
    const visibleIds = new Set();
    blood.forEach(id => {
      if (viewerIdSet.has(id)) visibleIds.add(viewerMember.id);
      else visibleIds.add(id);
    });
    spousesShown.forEach(id => {
      if (viewerIdSet.has(id)) return; // viewer is never their own spouse
      visibleIds.add(id);
    });
    
    const visibleMembersRaw = allMembersFlat.filter(m => visibleIds.has(m.id));
    
    // VIEWER ALIAS MERGE: the walker above used unionParentIds to traverse
    // the viewer's bloodline across all their alias rows, but downstream
    // consumers (FamilyTree, Edit Member modal) only see the ONE primary
    // viewer row and read its literal parent_ids / spouse_id. If the auto-
    // link wrote parent_ids to the viewer's personal-family _self row but
    // the primary row is the in-laws' row (because form.family_id points
    // there), FamilyTree sees parent_ids=[] and puts the viewer at gen 1 —
    // then the viewer's real parents render BELOW them (the Fathimath bug).
    //
    // Fix: merge parent_ids (union) and spouse_id (first non-null) from
    // every alias row INTO the primary viewer row, in-place on the output.
    const mergedViewerRow = (() => {
      const allParents = new Set();
      viewerRows.forEach(r => (r.parent_ids || []).forEach(pid => allParents.add(pid)));
      let mergedSpouse = viewerMember.spouse_id || null;
      if (!mergedSpouse) {
        for (const r of viewerRows) {
          if (r.spouse_id) { mergedSpouse = r.spouse_id; break; }
        }
      }
      return {
        ...viewerMember,
        parent_ids: [...allParents],
        spouse_id: mergedSpouse,
      };
    })();
    
    // Swap the primary viewer row in visibleMembersRaw for the merged one.
    const visibleMembersWithMerge = visibleMembersRaw.map(m =>
      m.id === viewerMember.id ? mergedViewerRow : m
    );
    
    // ALIAS-AWARE OUTPUT: for every voter-linked member in the output,
    // merge parent_ids / spouse_id from ALL of their alias rows across
    // ALL families (not just the ones pulled into visibleMembersRaw).
    // Without this, a sibling like Gamariyyath — whose _self row has no
    // spouse_id and no children pointing at it, but whose in-laws' row
    // has spouse_id=Abbas and kids pointing at it — renders as a lone
    // node. The + expand button then has nothing to reveal and disappears.
    //
    // Also handles non-viewer dedup (Aishath-Zoona-appears-twice in Full
    // Tree): picks one winner per voter_id, remaps references from losers
    // to the winner's id in other members' parent_ids / spouse_id.
    //
    // Winner selection per voter_id: prefer the row whose family_id
    // matches the viewer's primary family (myFamily.id); else first row.
    const visibleMembers = (() => {
      const primaryFamilyId = myFamily?.id || null;
      const winnerByVoterId = new Map();       // voter_id -> winner row (from visible set)
      const idRemap = new Map();                // loser.id -> winner.id
      const noVoterRows = [];
      
      visibleMembersWithMerge.forEach(m => {
        if (!m.voter_id) {
          noVoterRows.push(m);
          return;
        }
        const existing = winnerByVoterId.get(m.voter_id);
        if (!existing) {
          winnerByVoterId.set(m.voter_id, m);
          return;
        }
        // Collision within visible set: decide winner.
        let winner = existing;
        let loser = m;
        if (primaryFamilyId && m.family_id === primaryFamilyId && existing.family_id !== primaryFamilyId) {
          winner = m;
          loser = existing;
          winnerByVoterId.set(m.voter_id, m);
        }
        idRemap.set(loser.id, winner.id);
      });
      
      // ALSO: for any voter-linked winner, any OTHER alias row (in
      // allMembersFlat but NOT in visibleMembers) should remap to the
      // winner's id. So references from outside-family rows still resolve
      // inside the rendered tree.
      winnerByVoterId.forEach((winner, voterId) => {
        allMembersFlat.forEach(m => {
          if (m.voter_id === voterId && m.id !== winner.id) {
            idRemap.set(m.id, winner.id);
          }
        });
      });
      
      // Apply remap AND merge relationships across ALL aliases.
      // For each kept row, union parent_ids and pick first non-null
      // spouse_id across all its alias rows, then remap all loser-id
      // references to their winner's id.
      const remap = (id) => idRemap.has(id) ? idRemap.get(id) : id;
      const kept = [...winnerByVoterId.values(), ...noVoterRows];
      return kept.map(m => {
        // Gather all alias rows for this member (across all families).
        let aliasRows = [m];
        if (m.voter_id) {
          aliasRows = allMembersFlat.filter(x => x.voter_id === m.voter_id);
        }
        
        // Union parent_ids across aliases, then remap and dedup.
        const allParents = new Set();
        aliasRows.forEach(r => (r.parent_ids || []).forEach(pid => allParents.add(pid)));
        const remappedParents = [...allParents].map(remap);
        const dedupedParents = [...new Set(remappedParents)];
        
        // Spouse: prefer m's own spouse_id if set, else first non-null
        // across aliases. Then remap.
        let mergedSpouse = m.spouse_id || null;
        if (!mergedSpouse) {
          for (const r of aliasRows) {
            if (r.spouse_id) { mergedSpouse = r.spouse_id; break; }
          }
        }
        const remappedSpouse = mergedSpouse ? remap(mergedSpouse) : mergedSpouse;
        
        return { ...m, parent_ids: dedupedParents, spouse_id: remappedSpouse };
      });
    })();
    
    // Return a synthetic family object shaped like a normal one so every
    // existing consumer (FamilyTree, Manage Members, Edit modal dropdowns)
    // keeps working without changes.
    return {
      id: myFamily?.id || viewerMember.family_id || null,
      name: myFamily?.name || `${form.name?.split(' ')[0] || 'Family'} Family`,
      members: visibleMembers,
    };
  })();

  // Immediate-family slice of treeFamily — used by Manage Members and the
  // Add/Edit Member "Parents" / "Spouse" dropdowns so those modals don't
  // dump the entire extended family on the user. Same algorithm the family
  // tree uses for the Immediate view (parents + siblings + viewer's
  // children + spouse).
  const immediateMembers = treeFamily && treeFamily.members
    ? getImmediateFamily(treeFamily.members, voter.id, [], false)
    : [];
  
  // Is the viewer "married-in" to the family their family_id points at?
  // i.e. are they in that family ONLY as a spouse of a blood relative, with
  // no blood relatives of their own in that family_id group? If so, the
  // + Add Member button must NOT add to that family_id — it should create
  // a new family record for the viewer instead (so their in-laws don't get
  // polluted with the viewer's own relatives).
  // Is the viewer "married-in" to the family their family_id points at?
  // i.e. are they in that family ONLY as a spouse of a blood relative, with
  // no blood relatives of their own in that family_id group? If so, the
  // + Add Member button must NOT add to that family_id — it should create
  // a new family record for the viewer instead (so their in-laws don't get
  // polluted with the viewer's own relatives).
  //
  // The detection MUST be bidirectional on spouse_id: in real data, spouse
  // links are often one-directional (e.g. Abbas.spouse_id = Azeeza but
  // Azeeza.spouse_id = null). Without bidirectional lookup, a married-in
  // spouse with a null spouse_id looks like a "regular" member of their
  // linked family and the check fails open, routing the add to the wrong
  // family. That was the Azeeza bug.
  const viewerIsMarriedInToLinkedFamily = (() => {
    if (!myFamily || !myFamily.members) return false;
    const self = myFamily.members.find(m => m.voter_id === voter.id);
    if (!self) return false;
    
    // IMPORTANT: parent_ids in the DB can contain STALE references — ids of
    // members that were deleted but never cleaned up from other rows'
    // parent_ids arrays. If we count stale ids as "has a parent," we flag
    // people as blood-members (or as having a blood-member spouse) when
    // they're actually married-in. Filter every parent_ids read through a
    // liveness check: the parent must still exist as a row in this family.
    const livingIds = new Set(myFamily.members.map(m => m.id));
    const livingParents = (m) => (m.parent_ids || []).filter(pid => livingIds.has(pid));
    
    const myLivingParents = livingParents(self);
    
    // Rule 1: if I have any LIVING parent in this family, I'm a blood member.
    if (myLivingParents.length > 0) return false;
    
    // Rule 2: if I have any sibling in this family (anyone sharing at least
    // one LIVING parent with me), I'm a blood member. This catches the case
    // where my parents aren't in the family but my siblings are. Skipped if
    // I have no living parents — nothing to share.
    if (myLivingParents.length > 0) {
      const siblingInFamily = myFamily.members.some(m => {
        if (m.id === self.id) return false;
        const theirLiving = livingParents(m);
        return theirLiving.some(pid => myLivingParents.includes(pid));
      });
      if (siblingInFamily) return false;
    }
    
    // Rule 3: find my spouse BIDIRECTIONALLY. If I point at them OR they
    // point at me, we're a couple. Either direction is enough.
    let spouse = null;
    if (self.spouse_id) {
      spouse = myFamily.members.find(m => m.id === self.spouse_id);
    }
    if (!spouse) {
      spouse = myFamily.members.find(m => m.spouse_id === self.id);
    }
    
    // Rule 4: if my spouse exists in this family AND my spouse has LIVING
    // parents in this family (they're a blood member), then this is my
    // spouse's family, not mine. I'm married-in. Stale parent_ids on the
    // spouse are ignored — if all of their listed parents have been deleted,
    // they look like a co-founder (top of tree) and neither of us is
    // married-in.
    if (spouse) {
      const spouseLivingParents = livingParents(spouse);
      if (spouseLivingParents.length > 0) return true;
      
      // Also: if spouse has siblings in this family sharing a LIVING parent,
      // the family still "belongs to" the spouse's side.
      if (spouseLivingParents.length > 0) {
        const spouseSiblingInFamily = myFamily.members.some(m => {
          if (m.id === spouse.id || m.id === self.id) return false;
          const theirLiving = livingParents(m);
          return theirLiving.some(pid => spouseLivingParents.includes(pid));
        });
        if (spouseSiblingInFamily) return true;
      }
    }
    
    // No parents, no siblings, no spouse-with-family-presence → top-of-tree
    // couple (both co-founders) or brand-new viewer. Default to "not
    // married-in" — the add will go to the existing family. This is the
    // correct behavior for the Hariyya case: Raoof has no living parents,
    // so Hariyya is treated as a co-founder, not a married-in spouse.
    return false;
  })();
  
  // The family_id to write to when adding a member from the current view.
  // null means "create a new family for the viewer".
  const addMemberTargetFamilyId = viewerIsMarriedInToLinkedFamily ? null : (myFamily?.id || null);

  const scC = canSeeVotes
    ? (SC[form.support] || SC['Undecided'])
    : SC['Undecided'];
  const pm = PM[form.party_status] || PM['none'];

  const handleTreeClick = (m, mv) => {
    // Fall back to the member row's own voter_id when the resolved voter
    // object isn't in the local cache (out-of-scope, still loading, stale,
    // or a recently-deleted orphan). The destination route fetches the
    // voter directly from the DB and surfaces "Voter not found" cleanly.
    const targetId = mv?.id ?? m?.voter_id;
    if (targetId && targetId !== voter.id) navigate(`/voter/${targetId}`);
  };

  const addMemberToFamily = async (famId, member) => {
    let finalFamId = famId;
    
    
    // If no target family was given, we need to find (or create) one for
    // the viewer. This path fires in two cases:
    //   1. The viewer has no family_id at all (brand new) — classic path.
    //   2. The viewer IS linked to a family, but as a married-in spouse
    //      whose in-laws' tree they're currently viewing. In that case
    //      the new member must belong to the viewer's own side, not the
    //      in-laws'.
    if (!famId) {
      // REUSE FIRST: before creating a new family, check whether the viewer
      // already has a personal family from a previous add. A "personal
      // family" is any family_members row with voter_id = viewer that
      // lives in a family OTHER than the in-laws' family the viewer is
      // currently linked to. Without this check, every add by a married-in
      // spouse creates a fresh shell family (the multi-Hariyya-Family bug).
      const inLawFamilyId = form.family_id || null;
      const { data: existingRows, error: existingErr } = await supabase
        .from('family_members')
        .select('id, family_id')
        .eq('voter_id', voter.id);
      
      if (existingErr) {
        console.error('[addMember] existing-rows lookup failed:', existingErr);
      }
      
      const personalRow = (existingRows || []).find(
        r => r.family_id && r.family_id !== inLawFamilyId
      );
      
      if (personalRow) {
        // Reuse the viewer's existing personal family. No new family record.
        finalFamId = personalRow.family_id;
        console.log('[addMember] reusing existing personal family', finalFamId);
      } else {
        // First-time add for this married-in spouse (or brand-new viewer).
        // Create a personal family for them.
        const newFam = {
          id: 'F' + Date.now(),
          name: `${form.name.split(' ')[0]} Family`,
          island: form.island,
          created_by: profile.id
        };
        const {data, error} = await supabase.from('families').insert(newFam).select().single();
        if (error || !data) {
          console.error('[addMember] failed to create family:', error);
          return;
        }
        finalFamId = data.id;
        console.log('[addMember] created new personal family', finalFamId);
        
        // If the viewer has no family_id yet, update voters.family_id so the
        // top-bar label and analytics stay accurate. If the viewer already
        // had a family_id (married-in case), leave it alone — their link
        // into the in-laws' family is still meaningful.
        if (!form.family_id) {
          await supabase.from('voters').update({family_id: finalFamId}).eq('id', form.id);
          setForm(prev => ({...prev, family_id: finalFamId}));
        }
      }
      
      // Ensure the viewer has a _self row in the personal family, so the
      // new member can reference them as parent/spouse. Safe to run whether
      // the family was just created or reused — idempotent.
      const { data: existingSelf } = await supabase
        .from('family_members')
        .select('id')
        .eq('family_id', finalFamId)
        .eq('voter_id', voter.id)
        .maybeSingle();
      
      if (!existingSelf) {
        await supabase.from('family_members').insert({
          id: 'm' + Date.now() + '_self',
          family_id: finalFamId,
          role: 'Family Member',
          voter_id: voter.id,
          name: form.name,
          parent_ids: [],
          spouse_id: null,
        });
      }
      
      // Insert the new member into the chosen family. (In this branch the
      // modal skipped the insert because family_id was 'temp'; we do it
      // here now that we know the real family_id.)
      member.family_id = finalFamId;
      const { error: memErr } = await supabase.from('family_members').insert(member);
      if (memErr) {
        console.error('[addMember] failed to insert member:', memErr);
      }
    }
    
    // Bidirectional linking: if the new member is linked to a voter,
    // set that voter's family_id to this family (only if it isn't already
    // pointing at a different family — don't clobber existing links).
    if (member.voter_id && finalFamId) {
      const { data: existingVoter } = await supabase
        .from('voters')
        .select('family_id')
        .eq('id', member.voter_id)
        .single();
      if (existingVoter && !existingVoter.family_id) {
        await supabase.from('voters').update({family_id: finalFamId}).eq('id', member.voter_id);
      }
    }
    
    // AUTO-LINK on role=Parent: when the user adds a "Parent" from their
    // own profile, they almost always mean "this is MY parent." Without an
    // explicit link, the new parent record is a floating node with no edge
    // to the viewer, so it won't render in the viewer's bloodline walk.
    //
    // This block MUST fire for BOTH paths:
    //   - Normal path (famId set, viewer is blood / co-founder): viewer's
    //     row already exists in this family (any role — could be a _self
    //     row, or role=Parent, or role=Family Member). We match by voter_id.
    //   - Married-in path (famId null, personal family used/created above):
    //     viewer's _self row was just ensured. We match by voter_id too.
    if (member.role === 'Parent' && finalFamId) {
      // Find ALL rows for this voter in this family. Compare voter_id loosely
      // (both as string) in case of int/text type inconsistency.
      const { data: selfRows, error: selfErr } = await supabase
        .from('family_members')
        .select('id, parent_ids')
        .eq('family_id', finalFamId)
        .eq('voter_id', voter.id);
      if (selfErr) console.error('[addMember] self-row lookup failed:', selfErr);
      let selfRow = (selfRows || [])[0] || null;

      // Recover path: viewer's _self row was somehow lost (deleted/missing).
      // Re-create it so auto-link can proceed instead of silently failing.
      if (!selfRow) {
        const newSelfId = 'm' + Date.now() + '_self_recover';
        const { data: created, error: createErr } = await supabase
          .from('family_members')
          .insert({
            id: newSelfId,
            family_id: finalFamId,
            role: 'Family Member',
            voter_id: voter.id,
            name: form.name,
            parent_ids: [],
            spouse_id: null,
          })
          .select('id, parent_ids')
          .single();
        if (createErr) console.error('[addMember] recovery insert failed:', createErr);
        if (created) {
          selfRow = created;
          console.log('[addMember] recovered missing _self row as', newSelfId);
        }
      }

      if (selfRow) {
        const rawParents = selfRow.parent_ids || [];
        // STALE-REF FILTER: drop ids whose family_members rows no longer exist.
        let livingParents = rawParents;
        if (rawParents.length > 0) {
          const { data: existing } = await supabase
            .from('family_members').select('id').in('id', rawParents);
          const livingSet = new Set((existing || []).map(r => r.id));
          livingParents = rawParents.filter(pid => livingSet.has(pid));
        }
        // If the filter dropped any, persist the cleaned list immediately so
        // future operations start from a consistent state.
        if (livingParents.length !== rawParents.length) {
          await supabase.from('family_members').update({ parent_ids: livingParents }).eq('id', selfRow.id);
          console.log('[addMember] persisted cleaned parent_ids:', livingParents);
        }

        // Append the new parent if there's room and it isn't already there.
        if (livingParents.length < 2 && !livingParents.includes(member.id)) {
          const newParents = [...livingParents, member.id];
          const { error: updErr } = await supabase
            .from('family_members').update({ parent_ids: newParents }).eq('id', selfRow.id);
          if (updErr) console.error('[addMember] auto-link update failed:', updErr);
          else console.log('[addMember] auto-linked parent', member.id, 'to viewer row', selfRow.id, '→', newParents);
        } else if (livingParents.includes(member.id)) {
          console.log('[addMember] parent already linked, no-op');
        } else {
          // Both parent slots full (with live members). Replace the oldest in
          // place — better UX than silently dropping the user's add.
          const newParents = [...livingParents.slice(1), member.id];
          await supabase.from('family_members').update({ parent_ids: newParents }).eq('id', selfRow.id);
          console.log('[addMember] both parent slots full — replaced oldest with', member.id);
        }
      } else {
        console.warn('[addMember] auto-link: still no viewer row after recovery attempt in family', finalFamId);
      }
    }
    
    // AUTO-LINK on role='Family Member' with no explicit relationship set:
    // treat the new member as a child of the viewer so the walk algorithm
    // can reach them without the user having to manually set parent_ids
    // in the modal (which is empty on the very first add).
    if (member.role === 'Family Member' && finalFamId && !(member.parent_ids?.length) && !member.spouse_id) {
      const { data: selfRows } = await supabase
        .from('family_members')
        .select('id')
        .eq('family_id', finalFamId)
        .eq('voter_id', voter.id);
      const selfRow = (selfRows || [])[0] || null;
      if (selfRow) {
        const { error: fmLinkErr } = await supabase
          .from('family_members')
          .update({ parent_ids: [selfRow.id] })
          .eq('id', member.id);
        if (fmLinkErr) {
          console.error('[addMember] Family Member auto-link failed:', fmLinkErr);
        } else {
          console.log('[addMember] auto-linked Family Member', member.id, 'under viewer row', selfRow.id);
        }
      }
    }

    // Reload families and voters to reflect updates. Bubble to parent so the
    // cached prop stays current for subsequent navigations.
    if (refreshFamilies) await refreshFamilies();
    const vData = await fetchAllVoters(supabase);
    if (vData) setLocalVoters(vData);
    
    setShowAddMember(false);
  };

  return (
    <div style={{minHeight:'100vh', background:'var(--bg)', display:'flex', flexDirection:'column', overflowX:'hidden'}}>
      {/* Breadcrumb bar — portalled into the sticky main header so it sticks with no gap */}
      {headerEl && createPortal(
        <div style={{
          background:'var(--surface)',
          borderTop:'1px solid var(--border)',
          padding: isMobile ? '8px 12px' : '9px 22px',
          display:'flex', alignItems:'center', gap:8,
        }}>
          {/* Back button — always visible, never scrolls */}
          <button onClick={onClose} style={{background:'none', border:'1px solid var(--border)', color:'var(--text-2)', borderRadius:7, padding:'3px 9px', cursor:'pointer', fontSize:11, flexShrink:0}}>← Back</button>
          {/* Scrollable breadcrumb section */}
          <div style={{
            display:'flex', alignItems:'center', gap: isMobile ? 6 : 10,
            flex:1, minWidth:0,
            overflowX: isMobile ? 'auto' : 'visible',
            WebkitOverflowScrolling:'touch',
            flexWrap: isMobile ? 'nowrap' : 'wrap',
          }}>
            {!isMobile && <><span style={{color:'var(--surface-3)', fontSize:11, flexShrink:0}}>Voter List</span><span style={{color:'var(--text-4)', flexShrink:0}}>›</span></>}
            <span style={{color:'var(--link)', fontSize:11, fontWeight:600, whiteSpace:'nowrap', flexShrink:0}}>{voter.name}</span>
            {voter.island && <><span style={{color:'var(--text-4)', flexShrink:0}}>›</span><span style={{color:'var(--text-2)', fontSize:11, whiteSpace:'nowrap', flexShrink:0}}>{voter.island}</span></>}
            {voter.ward && <><span style={{color:'var(--text-4)', flexShrink:0}}>›</span><span style={{color:'var(--text-2)', fontSize:11, whiteSpace:'nowrap', flexShrink:0}}>{voter.ward}</span></>}
            {voter.house && <><span style={{color:'var(--text-4)', flexShrink:0}}>›</span><span style={{color:'var(--text-2)', fontSize:11, whiteSpace:'nowrap', flexShrink:0}}>{voter.house}</span></>}
            {voter.id_number && <><span style={{color:'var(--text-4)', flexShrink:0}}>›</span><span style={{color:'var(--text-2)', fontSize:11, whiteSpace:'nowrap', flexShrink:0}}>{voter.id_number}</span></>}
          </div>
          {/* Edit/Cancel/Save — always visible, never scrolls */}
          <div style={{display:'flex', gap:6, flexShrink:0}}>
            {!editing && canEdit && (
              <button onClick={() => setEditing(true)} style={{padding:'3px 11px', borderRadius:7, border:'1px solid var(--border)', background:'var(--border)', color:'var(--link)', cursor:'pointer', fontSize:11}}>Edit</button>
            )}
            {editing && (
              <>
                <button onClick={() => {setEditing(false); setForm({...voter});}} style={{padding:'3px 9px', borderRadius:7, border:'1px solid var(--border)', background:'transparent', color:'var(--text-2)', cursor:'pointer', fontSize:11}}>Cancel</button>
                <button onClick={saveVoter} style={{padding:'3px 11px', borderRadius:7, border:'1px solid #3b82f6', background:'var(--border)', color:'var(--link)', cursor:'pointer', fontSize:11}}>Save</button>
              </>
            )}
          </div>
        </div>,
        headerEl
      )}

      {/* Main content - responsive: two columns on desktop, stacked on mobile */}
      <div style={{flex:1, padding: isMobile ? '12px' : '20px', maxWidth:1200, margin:'0 auto', width:'100%', boxSizing:'border-box'}}>
        <div style={{
          display:'grid',
          gridTemplateColumns: isMobile ? '1fr' : '320px 1fr',
          gap: isMobile ? 12 : 20,
          alignItems:'start'
        }}>
          
          {/* LEFT COLUMN - 320px fixed.
              minWidth:0 is critical: without it, CSS Grid items default to
              min-width:auto and expand to fit their content, which can blow
              out the page width on mobile when a child element (like the
              family tree SVG) is wider than the available column. */}
          <div style={{display:'flex', flexDirection:'column', gap:12, minWidth: 0}}>
            {/* Photo + Name */}
            <Card>
              <div style={{display:'flex', flexDirection:'column', alignItems:'center', gap:10}}>
                <div style={{position:'relative'}}>
                  <div style={{
                    width:100, height:100, borderRadius:'50%',
                    background: 'var(--border-2)',
                    display:'flex', alignItems:'center', justifyContent:'center',
                    fontSize:32, fontWeight:'700', color:'var(--text-2)',
                    border:'3px solid ' + scC.dot,
                    opacity: photoUploading ? 0.5 : 1,
                    overflow:'hidden'
                  }}>
                    {voterPhotoUrl ? (
                      <img
                        src={voterPhotoUrl}
                        alt={voter.name}
                        style={{
                          width:'100%', height:'100%',
                          objectFit:'cover', objectPosition:'center',
                          display:'block'
                        }}
                      />
                    ) : (
                      voter.name.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase()
                    )}
                  </div>
                  {editing && canEdit && (
                    <>
                      <input type="file" ref={fileRef} onChange={handlePhoto} accept="image/*" style={{display:'none'}} />
                      <button
                        onClick={() => fileRef.current?.click()}
                        disabled={photoUploading}
                        style={{
                          position:'absolute', bottom:-5, right:-5, width:30, height:30, borderRadius:'50%',
                          border:'2px solid var(--surface)', background:'var(--border)', color:'var(--link)',
                          cursor: photoUploading ? 'wait' : 'pointer',
                          fontSize:14, display:'flex', alignItems:'center', justifyContent:'center'
                        }}>
                        {photoUploading ? '…' : <Icon name="upload" size={14} />}
                      </button>
                    </>
                  )}
                </div>
                {photoError && (
                  <div style={{fontSize:10, color:'#ef4444', textAlign:'center', maxWidth:140}}>
                    {photoError}
                  </div>
                )}
                
                {editing && canEdit ? (
                  <input value={form.name} onChange={e => up('name', e.target.value)} style={{...S.inp, fontSize:14, textAlign:'center', fontWeight:600}} />
                ) : (
                  <div style={{fontSize:16, fontWeight:600, color:'var(--text-strong)', textAlign:'center'}}>{form.name}</div>
                )}
              </div>
            </Card>

            {/* Basic info */}
            {/* Basic Info — identity + location + contact merged into one card */}
            <MobileCollapse title="Basic Info" isMobile={isMobile} defaultOpen={true}>
              <PRow label="ID Number" value={form.id_number || '—'} />
              <PRow label="Date of Birth" value={form.dob || '—'} />
              <PRow label="Age" value={calculateAge(form.dob) ?? form.age ?? '—'} />
              <PRow label="Gender" value={form.gender || '—'} />
              {editing && canEdit && (
                <div style={{marginTop:10, marginBottom:6}}>
                  <select value={form.gender || ''} onChange={e => up('gender', e.target.value)} style={S.sel}>
                    <option value="">Select gender</option>
                    {GENDER_OPTIONS.map(g => <option key={g} value={g}>{g}</option>)}
                  </select>
                </div>
              )}

              {editing && canEdit ? (
                <>
                  <MF label="Atoll / City">
                    <SearchableSelect
                      value={form.atoll || ''}
                      options={[...new Set(allLocations.map(l => l.atoll))].sort()}
                      onChange={v => up('atoll', v)}
                      placeholder="Select or type atoll / city"
                    />
                  </MF>
                  <MF label="Island">
                    <SearchableSelect
                      value={form.island || ''}
                      options={[...new Set(
                        allLocations
                          .filter(l => !form.atoll || l.atoll === form.atoll)
                          .map(l => l.island)
                      )].sort()}
                      onChange={v => up('island', v)}
                      placeholder="Select or type island"
                    />
                  </MF>
                  <MF label="Ward">
                    <input value={form.ward || ''} onChange={e => up('ward', e.target.value)} placeholder="Ward" style={S.inp} />
                  </MF>
                  <MF label="House">
                    <input value={form.house || ''} onChange={e => up('house', e.target.value)} placeholder="House name/number" style={S.inp} />
                  </MF>
                  <MF label="Current Location">
                    <input value={form.current_location || ''} onChange={e => up('current_location', e.target.value)} placeholder="If living elsewhere" style={S.inp} />
                  </MF>
                  <MF label="Ballot Box — Round 1">
                    <SearchableSelect
                      value={form.ballot_box_r1 || ''}
                      options={ballotBoxes}
                      onChange={v => up('ballot_box_r1', v)}
                      placeholder="Select Round 1 ballot box"
                      onAddNew={canAddBallotBox ? addBallotBox : undefined}
                      addNewLabel="Add new ballot box"
                    />
                  </MF>
                  <MF label="Ballot Box — Round 2">
                    <SearchableSelect
                      value={form.ballot_box_r2 || ''}
                      options={ballotBoxes}
                      onChange={v => up('ballot_box_r2', v)}
                      placeholder="Select Round 2 ballot box"
                      onAddNew={canAddBallotBox ? addBallotBox : undefined}
                      addNewLabel="Add new ballot box"
                    />
                  </MF>
                  <MF label="Phone"><input value={form.phone || ''} onChange={e => up('phone', e.target.value)} placeholder="Primary phone" style={S.inp} /></MF>
                  <MF label="Phone 2"><input value={form.phone2 || ''} onChange={e => up('phone2', e.target.value)} placeholder="Secondary phone" style={S.inp} /></MF>
                  <MF label="Phone 3"><input value={form.phone3 || ''} onChange={e => up('phone3', e.target.value)} placeholder="Third phone" style={S.inp} /></MF>
                  <MF label="WhatsApp"><input value={form.whatsapp || ''} onChange={e => up('whatsapp', e.target.value)} placeholder="WhatsApp number" style={S.inp} /></MF>
                  <MF label="Email"><input value={form.email || ''} onChange={e => up('email', e.target.value)} placeholder="Email address" style={S.inp} /></MF>
                </>
              ) : (
                <>
                  {form.atoll && <PRow label="Atoll / City" value={form.atoll} />}
                  <PRow label="Island" value={form.island} />
                  <PRow label="Ward" value={form.ward || '—'} />
                  <PRow label="House" value={form.house || '—'} />
                  {form.current_location && <PRow label="Current Location" value={form.current_location} />}
                  {form.ballot_box_r1 && <PRow label="Ballot Box R1" value={form.ballot_box_r1} />}
                  {form.ballot_box_r2 && <PRow label="Ballot Box R2" value={form.ballot_box_r2} />}
                  <PRow label="Phone" value={form.phone || '—'} />
                  {form.phone2 && <PRow label="Phone 2" value={form.phone2} />}
                  {form.phone3 && <PRow label="Phone 3" value={form.phone3} />}
                  {form.whatsapp && <PRow label="WhatsApp" value={form.whatsapp} />}
                  {form.email && <PRow label="Email" value={form.email} />}
                </>
              )}

              {/* Social Media — merged into Basic Info so identity, location,
                  contact, and social all live in one card. */}
              <div style={{fontSize:9, color:'var(--text-4)', fontFamily:"'DM Mono',monospace", textTransform:'uppercase', letterSpacing:0.5, marginTop:14, marginBottom:4, paddingTop:10, borderTop:'1px solid var(--surface-2)'}}>
                Social Media
              </div>
              {[
                ['Facebook', 'social_facebook', 'facebook', '#1877f2', 'https://facebook.com/'],
                ['Instagram', 'social_instagram', 'instagram', '#e1306c', 'https://instagram.com/'],
                ['TikTok', 'social_tiktok', 'tiktok', 'var(--text-2)', 'https://tiktok.com/@'],
                ['Twitter/X', 'social_twitter', 'twitter', '#1d9bf0', 'https://x.com/']
              ].map(([label, key, iconName, brandColor, base]) => {
                const handle = form[key];
                const clean = handle ? handle.replace(/^@/, '') : '';
                const url = clean ? base + clean : null;
                return (
                  <div key={key} style={{display:'flex', justifyContent:'space-between', alignItems:'center', padding:'5px 0', borderBottom:'1px solid var(--surface-2)'}}>
                    <span style={{color:'var(--text-4)', fontSize:10, flexShrink:0, display:'inline-flex', alignItems:'center', gap:6}}><Icon name={iconName} size={13} color={brandColor} /> {label}</span>
                    <div style={{display:'flex', alignItems:'center', gap:6}}>
                      {editing && canEdit
                        ? <input value={form[key] || ''} onChange={e => up(key, e.target.value)} placeholder="@username" style={{...S.minInp, width:120}} />
                        : url
                          ? <><a href={url} target="_blank" rel="noopener noreferrer" style={{color:'var(--link)', fontSize:10, textDecoration:'none', borderBottom:'1px dashed #7dd3fc44'}}>{handle}</a>
                             <a href={url} target="_blank" rel="noopener noreferrer" style={{color:'var(--text-3)', fontSize:9, textDecoration:'none'}}>↗</a></>
                          : <span style={{color:'var(--surface-3)', fontSize:10}}>—</span>
                      }
                    </div>
                  </div>
                );
              })}
            </MobileCollapse>

            {/* Occupation */}
            {canEdit && (
              <MobileCollapse title="Occupation" isMobile={isMobile}>
                {editing ? (
                  <>
                    <MF label="Job Title">
                      <input value={form.job || ''} onChange={e => up('job', e.target.value)} placeholder="Job title" style={S.inp} />
                    </MF>
                    <MF label="Category">
                      <SearchableSelect
                        value={form.occupation_category || ''}
                        options={OCCUPATION_CATEGORIES}
                        onChange={v => up('occupation_category', v)}
                        placeholder="Select category"
                      />
                    </MF>
                  </>
                ) : (
                  <>
                    <PRow label="Job" value={form.job || '—'} />
                    <PRow label="Category" value={form.occupation_category || '—'} />
                  </>
                )}
              </MobileCollapse>
            )}

            {/* Support level badge — compact inline pill. Editing opens a
                button list below to pick a new level. */}
            {canSeeVotes && (
              <Card>
                <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', gap:8}}>
                  <div style={{color:'var(--link)', fontSize:11, fontWeight:600, letterSpacing:0.3}}>
                    Support Level
                  </div>
                  <div style={{display:'flex', alignItems:'center', gap:6}}>
                    {!editing && canSeeVotes && !canEdit && !editingVotes && (
                      <button onClick={() => setEditingVotes(true)} style={{padding:'3px 10px', borderRadius:7, border:'1px solid var(--border)', background:'var(--border)', color:'var(--link)', cursor:'pointer', fontSize:10}}>Edit</button>
                    )}
                    {editingVotes && (
                      <>
                        <button onClick={() => {setEditingVotes(false); setForm({...voter});}} style={{padding:'3px 9px', borderRadius:7, border:'1px solid var(--border)', background:'transparent', color:'var(--text-2)', cursor:'pointer', fontSize:10}}>Cancel</button>
                        <button onClick={saveVoter} style={{padding:'3px 10px', borderRadius:7, border:'1px solid #3b82f6', background:'var(--border)', color:'var(--link)', cursor:'pointer', fontSize:10}}>Save</button>
                      </>
                    )}
                    <span style={{
                      padding:'3px 10px', borderRadius:20,
                      background: scC.bg, color: scC.text,
                      fontSize:10, fontWeight:600, whiteSpace:'nowrap'
                    }}>
                      <span style={{display:'inline-block', width:6, height:6, borderRadius:'50%', background:scC.dot, marginRight:5, verticalAlign:'middle'}} />
                      {form.support || 'Undecided'}
                    </span>
                  </div>
                </div>
                {((editing && canEditVotes_) || editingVotes) && (
                  <div style={{marginTop:10, display:'flex', flexWrap:'wrap', gap:5}}>
                    {SUPPORT_LEVELS.map(level => {
                      const colors = SC[level];
                      const isActive = form.support === level;
                      return (
                        <button
                          key={level}
                          onClick={() => up('support', level)}
                          style={{
                            padding:'4px 9px',
                            borderRadius:14,
                            border: isActive ? `1.5px solid ${colors.dot}` : '1px solid var(--border)',
                            background: isActive ? colors.bg : 'transparent',
                            color: isActive ? colors.text : 'var(--text-3)',
                            cursor:'pointer',
                            fontSize:10,
                            fontWeight: isActive ? 600 : 400
                          }}>
                          <span style={{display:'inline-block', width:6, height:6, borderRadius:'50%', background:colors.dot, marginRight:4, verticalAlign:'middle'}} />
                          {level}
                        </button>
                      );
                    })}
                  </div>
                )}
              </Card>
            )}

            {/* Support edit history — leaders only */}
            {['super_admin', 'campaign_manager', 'candidate'].includes(profile?.role) && supportHistory.length > 0 && (
              <Card>
                <div style={{fontSize:11, fontWeight:600, color:'var(--text-3)', letterSpacing:'0.05em', marginBottom:8}}>SUPPORT HISTORY</div>
                <div style={{display:'flex', flexDirection:'column', gap:6}}>
                  {supportHistory.map((row, i) => {
                    const ago = (() => {
                      const diff = Date.now() - new Date(row.changed_at).getTime();
                      const m = Math.floor(diff / 60000);
                      if (m < 1) return 'just now';
                      if (m < 60) return `${m}m ago`;
                      const h = Math.floor(m / 60);
                      if (h < 24) return `${h}h ago`;
                      const d = Math.floor(h / 24);
                      return `${d}d ago`;
                    })();
                    return (
                      <div key={i} style={{display:'flex', alignItems:'center', gap:8, fontSize:12}}>
                        <span style={{color:'var(--text-2)', flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>
                          {row.editor?.full_name || 'Unknown'}
                        </span>
                        <span style={{color:'var(--text-3)', fontSize:11, whiteSpace:'nowrap'}}>
                          {row.changed_from || 'Undecided'} → {row.changed_to}
                        </span>
                        <span style={{color:'var(--text-4)', fontSize:10, whiteSpace:'nowrap'}}>{ago}</span>
                      </div>
                    );
                  })}
                </div>
              </Card>
            )}

            {/* Preferred candidate — shown only for against voters */}
            {(form.support === 'Leaning Against' || form.support === 'Strong Against') && (canEditVotes_ || canSeeVotes) && (
              <Card>
                <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', gap:8}}>
                  <div style={{color:'var(--sup-against-text)', fontSize:11, fontWeight:600, letterSpacing:0.3}}>
                    Preferred Candidate
                  </div>
                  <span style={{padding:'3px 10px', borderRadius:20, background:'var(--sup-against-bg)', color:'var(--sup-against-text)', fontSize:10, fontWeight:600, whiteSpace:'nowrap'}}>
                    {form.preferred_candidate || 'Unknown'}
                  </span>
                </div>
                {(editing || editingVotes) && (
                  <div style={{marginTop:10}}>
                    {opponentNames.length > 0 ? (
                      <SearchableSelect
                        value={form.preferred_candidate || ''}
                        options={opponentNames}
                        onChange={v => up('preferred_candidate', v || '')}
                        placeholder="Select opponent"
                      />
                    ) : (
                      <div>
                        <input
                          value={form.preferred_candidate || ''}
                          onChange={e => up('preferred_candidate', e.target.value)}
                          placeholder="Type opponent name (or add opponents in Analytics)"
                          style={{width:'100%', padding:'7px 10px', background:'var(--surface-2)', border:'1px solid var(--border)', borderRadius:6, color:'var(--text)', fontSize:12, boxSizing:'border-box'}}
                        />
                        <div style={{fontSize:10, color:'var(--text-4)', marginTop:4}}>Tip: add opponent names in Analytics → Support → Manage Opponents</div>
                      </div>
                    )}
                  </div>
                )}
              </Card>
            )}

            {/* Party status */}
            {hasEffectivePermission(profile, 'canViewParty') && (
              <Card>
                <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', gap:8}}>
                  <div style={{color:'var(--link)', fontSize:11, fontWeight:600, letterSpacing:0.3}}>
                    Party Status
                  </div>
                  <span style={{padding:'3px 10px', borderRadius:20, background:pm.bg, color:pm.color, fontSize:10, fontWeight:600, whiteSpace:'nowrap'}}>
                    {pm.label}
                  </span>
                </div>
                {editing && hasEffectivePermission(profile, 'canEditParty') && (
                  <div style={{marginTop:10}}>
                    <SearchableSelect
                      value={form.party_status || 'none'}
                      options={Object.entries(PM).map(([k, v]) => ({ value: k, label: v.label }))}
                      onChange={v => up('party_status', v || 'none')}
                      placeholder="Select party"
                    />
                  </div>
                )}
              </Card>
            )}

            {/* Social Media moved: now appears right after Contact. */}

            {/* Influencer toggle */}
            {canEdit && editing && (
              <Card>
                <label style={{display:'flex', alignItems:'center', gap:9, cursor:'pointer'}}>
                  <div onClick={() => up('influencer', !form.influencer)} style={{
                    width:36, height:19, borderRadius:10,
                    background: form.influencer ? '#1d4ed8' : 'var(--hover)',
                    border:'1px solid var(--border)', position:'relative', cursor:'pointer'
                  }}>
                    <div style={{
                      width:13, height:13, borderRadius:'50%',
                      background: form.influencer ? 'var(--link)' : 'var(--text-4)',
                      position:'absolute', top:2, left: form.influencer ? 19 : 2,
                      transition:'left 0.2s'
                    }} />
                  </div>
                  <span style={{color:'var(--text-2)', fontSize:11}}>Key influencer</span>
                </label>
              </Card>
            )}
          </div>

          {/* RIGHT COLUMN - Flexible. minWidth:0 for the same reason as LEFT. */}
          <div style={{display:'flex', flexDirection:'column', gap:12, minWidth: 0}}>
            {/* Family Tree */}
            {hasEffectivePermission(profile, 'canViewFamily') && (
              <Card>
                <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12}}>
                  <div>
                    <SecTitle t="Family Tree" />
                    <div style={{fontSize:9, color:'var(--text-3)', marginTop:2}}>Click members to navigate · Add family by linking to voters</div>
                  </div>
                  {canEdit && (
                    <button onClick={() => setShowAddMember(true)} style={{
                      padding:'4px 11px', borderRadius:7, border:'1px solid var(--border)',
                      background:'transparent', color:'var(--link)', cursor:'pointer', fontSize:10
                    }}>+ Add Member</button>
                  )}
                </div>
                {treeFamily && treeFamily.members && treeFamily.members.length > 0 ? (
                  <>
                    <FamilyTree
                      family={treeFamily}
                      voters={localVoters}
                      highlightId={voter.id}
                      onClickMember={handleTreeClick}
                      supabase={supabase}
                      canSeeSupport={canSeeVotes}
                    />
                    <div style={{fontSize:8, color:'var(--text-4)', marginTop:4, textAlign:'center', marginBottom:12}}>
                      CLICK MEMBERS TO NAVIGATE · ♥ = SPOUSE · CURVED LINE = PARENT-CHILD
                    </div>
                    {canEdit && (
                      <div style={{borderTop:'1px solid var(--border)', paddingTop:12}}>
                        {/* Collapsible Manage Members Header */}
                        <div 
                          onClick={() => setManageMembersExpanded(!manageMembersExpanded)}
                          style={{
                            display:'flex', 
                            justifyContent:'space-between', 
                            alignItems:'center',
                            cursor:'pointer',
                            marginBottom: manageMembersExpanded ? 8 : 0,
                            padding: '4px 0'
                          }}
                        >
                          <div style={{fontSize:9, color:'var(--link)', fontWeight:600}}>
                            {manageMembersExpanded ? '▼' : '▶'} Manage Members ({immediateMembers.length})
                          </div>
                        </div>

                        {/* Members List - Only show when expanded.
                            Limited to immediate family (parents + siblings +
                            children + viewer + spouse) so the list stays
                            manageable on big trees. Full extended family is
                            still visible/editable through Full Tree view. */}
                        {manageMembersExpanded && (
                          <div style={{display:'flex', flexDirection:'column', gap:6}}>
                            {immediateMembers.map(m => {
                              const linkedVoter = m.voter_id ? localVoters.find(v => v.id === m.voter_id) : null;
                              return (
                                <div key={m.id} style={{
                                  display:'flex', justifyContent:'space-between', alignItems:'center',
                                  padding:'6px 10px', background:'var(--surface)', borderRadius:7, border:'1px solid var(--border)'
                                }}>
                                  <div style={{flex:1}}>
                                    <div style={{fontSize:10, color:'var(--text)', fontWeight:500}}>{m.name}</div>
                                    <div style={{fontSize:8, color:'var(--text-3)', marginTop:1}}>
                                      {m.role}
                                      {linkedVoter ? (
                                        <span style={{color:'var(--link)', marginLeft:6}}>
                                          → Linked to {linkedVoter.name}
                                        </span>
                                      ) : (
                                        <span style={{color:'var(--text-3)', marginLeft:6, fontStyle:'italic'}}>
                                          · Manual entry
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  <div style={{display:'flex', gap:6}}>
                                    <button
                                      onClick={() => {
                                        setEditingMember(m);
                                        setEditVoterSearch('');
                                        setEditVoterResults([]);
                                        setChangingVoter(false);
                                        setShowEditMember(true);
                                      }}
                                      style={{
                                        padding:'4px 8px', borderRadius:5, border:'1px solid var(--border)',
                                        background:'rgba(59,130,246,0.12)', color:'var(--link)', cursor:'pointer', fontSize:9
                                      }}>
                                      Edit
                                    </button>
                                    <button
                                      onClick={async () => {
                                        if (!confirm(`Remove ${m.name} from family tree?`)) return;
                                        
                                        // Before deleting the row, clean up every other row that
                                        // references it. Use DB-side filters so we don't miss rows
                                        // past Supabase's 1000-row default page size.
                                        const [{ data: parentRefs }, { data: spouseRefs }] = await Promise.all([
                                          supabase.from('family_members').select('id, parent_ids').contains('parent_ids', [m.id]),
                                          supabase.from('family_members').select('id, spouse_id').eq('spouse_id', m.id),
                                        ]);
                                        const cleanupOps = [];
                                        for (const r of (parentRefs || [])) {
                                          cleanupOps.push(
                                            supabase.from('family_members')
                                              .update({ parent_ids: (r.parent_ids || []).filter(pid => pid !== m.id) })
                                              .eq('id', r.id)
                                          );
                                        }
                                        for (const r of (spouseRefs || [])) {
                                          cleanupOps.push(
                                            supabase.from('family_members')
                                              .update({ spouse_id: null })
                                              .eq('id', r.id)
                                          );
                                        }
                                        if (cleanupOps.length > 0) {
                                          await Promise.all(cleanupOps);
                                          console.log('[delete] cleaned', cleanupOps.length, 'ref(s) to', m.id);
                                        }
                                        
                                        const {error} = await supabase.from('family_members').delete().eq('id', m.id);
                                        if (!error && refreshFamilies) {
                                          await refreshFamilies();
                                        }
                                      }}
                                      style={{
                                        padding:'4px 8px', borderRadius:5, border:'1px solid rgba(239,68,68,0.5)',
                                        background:'rgba(239,68,68,0.12)', color:'#ef4444', cursor:'pointer', fontSize:9
                                      }}>
                                      Remove
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <div style={{color:'var(--text-3)', fontSize:11, padding:'20px 0', textAlign:'center'}}>
                    No family members yet. {canEdit && 'Click "+ Add Member" to start building the tree.'}
                  </div>
                )}
              </Card>
            )}

            {/* Outreach History */}
            {hasEffectivePermission(profile, 'canLogOutreach') && (
              <Card>
                <SecTitle t="Outreach History" />
                {/* Log form */}
                <OutreachLogForm
                  onSave={addOut}
                  canManage={canManageOutreachTypes}
                  customTypes={customOutreachTypes}
                  onAddType={addOutreachType}
                  onDeleteType={deleteOutreachType}
                  customOutcomes={customOutreachOutcomes}
                  onAddOutcome={addOutreachOutcome}
                  onDeleteOutcome={deleteOutreachOutcome}
                />
                {/* History list — by default only the latest entry is shown.
                    A "Show all (N)" toggle reveals the rest. Once expanded,
                    each entry shows its full content (type, date, outcome,
                    notes, delete button) — no per-entry collapsing. */}
                {outreach.length === 0 ? (
                  <div style={{color:'var(--surface-3)', fontSize:11, marginTop:8}}>No outreach yet.</div>
                ) : (
                  <>
                    {(showAllOutreach ? outreach : outreach.slice(0, 1)).map(o => (
                      <div key={o.id} style={{
                        padding:'8px 10px', borderRadius:7, background:'var(--bg)', marginBottom:4, fontSize:11
                      }}>
                        <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', gap:8}}>
                          <span style={{color:'var(--link)', fontWeight:500, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{o.type}</span>
                          <div style={{display:'flex', alignItems:'center', gap:8, flexShrink:0}}>
                            <span style={{color:'var(--text-4)', fontSize:10}}>{o.date}</span>
                            {canEdit && (
                              <button onClick={() => rmOut(o.id)} style={{
                                background:'none', border:'none', color:'#ef4444',
                                cursor:'pointer', fontSize:13, padding:'0 4px'
                              }} aria-label="Delete outreach entry">×</button>
                            )}
                          </div>
                        </div>
                        {o.outcome && <div style={{color:'var(--text-2)', fontSize:10, marginTop:3, display:'flex', alignItems:'center', gap:5}}><Icon name="flag" size={10} /> {o.outcome}</div>}
                        {o.notes && <div style={{color:'var(--text-3)', fontSize:10, marginTop:2, fontStyle:'italic'}}>"{o.notes}"</div>}
                      </div>
                    ))}

                    {/* Show all / Show less toggle — only when there's more
                        than one entry to hide/reveal. */}
                    {outreach.length > 1 && (
                      <button
                        onClick={() => setShowAllOutreach(s => !s)}
                        style={{
                          width:'100%',
                          marginTop:6,
                          padding:'7px 10px',
                          background:'transparent',
                          border:'1px dashed var(--border)',
                          borderRadius:7,
                          color:'var(--link)',
                          cursor:'pointer',
                          fontSize:10,
                          fontFamily: "'DM Mono', monospace"
                        }}>
                        {showAllOutreach
                          ? `▴ Show less`
                          : `▾ Show all (${outreach.length})`}
                      </button>
                    )}
                  </>
                )}
              </Card>
            )}

            {/* Issues — click to toggle, persists immediately (no Save button
                needed). Matches the Outreach pattern: direct, no edit-mode
                gating. Still gated by canEditInfo permission. */}
            {canEdit && (
              <Card>
                <SecTitle t="Key Issues" />
                <div style={{display:'flex', flexWrap:'wrap', gap:6}}>
                  {ISSUE_OPTIONS.map(i => {
                    const a = (form.issues || []).includes(i);
                    return (
                      <button
                        key={i}
                        onClick={async () => {
                          const current = form.issues || [];
                          const next = current.includes(i)
                            ? current.filter(x => x !== i)
                            : [...current, i];
                          // Optimistic local update for snappy UI.
                          up('issues', next);
                          // Persist immediately — no Save button dependence.
                          const { error } = await supabase
                            .from('voters')
                            .update({ issues: next })
                            .eq('id', voter.id);
                          if (error) {
                            console.error('[issues] save failed:', error);
                            // Roll back the optimistic update on failure.
                            up('issues', current);
                          }
                        }}
                        style={{
                          padding:'5px 11px', borderRadius:8,
                          border: a ? '1px solid #3b82f6' : '1px solid var(--border)',
                          background: a ? 'var(--border)' : 'transparent',
                          color: a ? 'var(--link)' : 'var(--text-3)',
                          cursor: 'pointer',
                          fontSize:11, fontWeight: a ? 600 : 500,
                          fontFamily: "'DM Mono', monospace"
                        }}>
                        {i}
                      </button>
                    );
                  })}
                </div>
              </Card>
            )}

            {/* Notes — always editable (no edit-mode gating). Saves are
                debounced ~600ms after the last keystroke and also on blur,
                so the user doesn't have to hit Save. Tiny status chip top-
                right shows saving/saved/error. */}
            {/* NOTES — multi-entry, mirrors Outreach.
                A "+ Add Note" textarea sits on top; saving inserts a new row
                in voter_notes and clears the input. Past notes are listed
                below, each in a Collapsible (closed by default).
                Legacy single-field `voter.notes` (if present from before the
                multi-notes migration) is shown as a read-only entry at the
                bottom so old data isn't hidden. */}
            {canEdit && (
              <Card>
                <SecTitle t="Notes" />

                {/* Add new note form */}
                <textarea
                  value={noteDraft}
                  onChange={e => setNoteDraft(e.target.value)}
                  rows={3}
                  placeholder="Write a new note about this voter…"
                  style={{
                    width:'100%', background:'var(--bg)', border:'1px solid var(--border)',
                    borderRadius:7, padding:9, color:'var(--text)', fontSize:11,
                    resize:'vertical', outline:'none', boxSizing:'border-box',
                    fontFamily:'inherit', marginBottom: 6
                  }}
                />
                <div style={{display:'flex', justifyContent:'flex-end', marginBottom: 10}}>
                  <button
                    onClick={addNote}
                    disabled={!noteDraft.trim() || noteSaving}
                    style={{
                      padding:'5px 14px',
                      background: noteDraft.trim() && !noteSaving ? 'var(--border)' : 'transparent',
                      border:'1px solid ' + (noteDraft.trim() && !noteSaving ? '#3b82f6' : 'var(--border)'),
                      color: noteDraft.trim() && !noteSaving ? 'var(--link)' : 'var(--text-4)',
                      borderRadius:6, fontSize:11,
                      cursor: noteDraft.trim() && !noteSaving ? 'pointer' : 'default',
                      fontFamily: "'DM Mono', monospace"
                    }}>
                    {noteSaving ? 'Saving…' : '+ Add Note'}
                  </button>
                </div>

                {/* List of saved notes — by default only the latest one is
                    shown. A "Show all (N)" toggle reveals the rest. Each
                    entry renders fully (no per-row collapse) so the user
                    sees the note text immediately, with timestamp + delete
                    aligned with the date+x pattern from Outreach. */}
                {voterNotes.length === 0 && !form.notes ? (
                  <div style={{color:'var(--surface-3)', fontSize:11}}>No notes yet.</div>
                ) : (
                  <>
                    {(showAllNotes ? voterNotes : voterNotes.slice(0, 1)).map(n => {
                      const dateStr = n.created_at ? new Date(n.created_at).toLocaleString(undefined, {
                        year:'numeric', month:'short', day:'numeric',
                        hour:'2-digit', minute:'2-digit'
                      }) : '';
                      return (
                        <div key={n.id} style={{
                          padding:'8px 10px', borderRadius:7, background:'var(--bg)', marginBottom:4, fontSize:11
                        }}>
                          <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', gap:8, marginBottom: 4}}>
                            <span style={{color:'var(--text-4)', fontSize:10}}>{dateStr}</span>
                            <button onClick={() => rmNote(n.id)} style={{
                              background:'none', border:'none', color:'#ef4444',
                              cursor:'pointer', fontSize:13, padding:'0 4px', flexShrink:0
                            }} aria-label="Delete note">×</button>
                          </div>
                          <div style={{color:'var(--text)', fontSize:11, whiteSpace:'pre-wrap'}}>
                            {n.note}
                          </div>
                        </div>
                      );
                    })}

                    {/* Show all / Show less toggle — only when there's more
                        than one note to hide/reveal. The legacy single-field
                        note (if present) counts toward the total too. */}
                    {(voterNotes.length + (form.notes && form.notes.trim() ? 1 : 0)) > 1 && (
                      <button
                        onClick={() => setShowAllNotes(s => !s)}
                        style={{
                          width:'100%',
                          marginTop:6,
                          padding:'7px 10px',
                          background:'transparent',
                          border:'1px dashed var(--border)',
                          borderRadius:7,
                          color:'var(--link)',
                          cursor:'pointer',
                          fontSize:10,
                          fontFamily: "'DM Mono', monospace"
                        }}>
                        {showAllNotes
                          ? `▴ Show less`
                          : `▾ Show all (${voterNotes.length + (form.notes && form.notes.trim() ? 1 : 0)})`}
                      </button>
                    )}

                    {/* Legacy single-field note — only shown when "Show all"
                        is on, since it's old data and shouldn't push the
                        latest current-format note out of view by default. */}
                    {showAllNotes && form.notes && form.notes.trim() && (
                      <div style={{
                        padding:'8px 10px', borderRadius:7, background:'var(--bg)', marginBottom:4, fontSize:11,
                        border:'1px dashed var(--border)'
                      }}>
                        <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', gap:8, marginBottom: 4}}>
                          <span style={{color:'var(--text-4)', fontSize:10, fontStyle:'italic'}}>legacy note</span>
                        </div>
                        <div style={{color:'var(--text-2)', fontSize:11, whiteSpace:'pre-wrap', fontStyle:'italic'}}>
                          {form.notes}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Add Member Modal - 2-Step Selection */}
      {showAddMember && (
        <AddMemberModal2Step
          familyId={addMemberTargetFamilyId}
          families={families}
          voters={localVoters}
          voter={voter}
          relatives={immediateMembers}
          onClose={() => setShowAddMember(false)}
          onSave={addMemberToFamily}
          supabase={supabase}
        />
      )}

      {/* Edit Member Modal - Link Spouse/Parents */}
      {showEditMember && editingMember && (() => {
        // CYCLE GUARD: members reachable by walking DOWN from editingMember
        // through parent_ids (children, grandchildren, ...). Picking any of
        // these as editingMember's own parent would create a cycle — the
        // layout code has cycle guards so it won't crash, but the tree
        // would render nonsense (a descendant drawn as an ancestor).
        const descendantIds = new Set();
        const allLocalMembers = treeFamily?.members || [];
        const cycleQueue = [editingMember.id];
        while (cycleQueue.length > 0) {
          const current = cycleQueue.shift();
          allLocalMembers.forEach(m => {
            if ((m.parent_ids || []).includes(current) && !descendantIds.has(m.id)) {
              descendantIds.add(m.id);
              cycleQueue.push(m.id);
            }
          });
        }

        return (
        <div style={{
          position:'fixed', inset:0, background:'rgba(0,0,0,0.85)',
          zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center',
          padding:16
        }}>
          <Card style={{width:'100%', maxWidth:500, padding:20}}>
            <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16}}>
              <div style={{fontFamily:"'Cormorant Garamond',serif", fontSize:17, color:'var(--text-strong)'}}>
                Edit: {editingMember.name}
              </div>
              <button 
                onClick={() => {setShowEditMember(false); setEditingMember(null); setChangingVoter(false);}}
                style={{
                  background:'none', border:'none', color:'var(--text-3)', 
                  cursor:'pointer', fontSize:20, padding:'0 4px'
                }}>×</button>
            </div>

            <div style={{fontSize:9, color:'var(--text-3)', marginBottom:16}}>
              Link this family member to a voter, spouse, and parents
            </div>

            {/* VOTER LINKING — controls navigability from family tree */}
            <MF label="Linked Voter">
              {(() => {
                const currentLinked = editingMember.voter_id
                  ? localVoters.find(v => v.id === editingMember.voter_id)
                  : null;
                
                // State A: voter linked, not currently changing — show the
                // green "Current" card with Unlink + Change Person buttons.
                if (currentLinked && !changingVoter) {
                  return (
                    <div style={{
                      background:'rgba(16,185,129,0.12)', border:'1px solid #10b981', borderRadius:6,
                      padding:10, display:'flex', justifyContent:'space-between', alignItems:'center', gap:8
                    }}>
                      <div style={{minWidth:0, flex:1}}>
                        <div style={{fontSize:11, color:'var(--text-strong)', fontWeight:600, display:'flex', alignItems:'center', gap:5}}>
                          <Icon name="check" size={12} color="#10b981" /> {currentLinked.name}
                        </div>
                        <div style={{fontSize:9, color:'var(--text-3)', marginTop:2}}>
                          {currentLinked.island}{currentLinked.ward ? ', ' + currentLinked.ward : ''}
                        </div>
                      </div>
                      <div style={{display:'flex', gap:6, flexShrink:0}}>
                        <button
                          onClick={() => {
                            // Stage a swap: keep voter_id unchanged until the
                            // user picks a new voter, but reveal search UI.
                            setChangingVoter(true);
                            setEditVoterSearch('');
                            setEditVoterResults([]);
                          }}
                          style={{
                            padding:'4px 10px', background:'transparent', border:'1px solid #38bdf8',
                            borderRadius:4, color:'var(--link)', cursor:'pointer', fontSize:10
                          }}>
                          Change Person
                        </button>
                        <button
                          onClick={() => setEditingMember({...editingMember, voter_id: null})}
                          style={{
                            padding:'4px 10px', background:'transparent', border:'1px solid #10b981',
                            borderRadius:4, color:'#10b981', cursor:'pointer', fontSize:10
                          }}>
                          Unlink
                        </button>
                      </div>
                    </div>
                  );
                }
                
                // State B: voter linked AND user clicked Change Person —
                // show the current link (badged) AND the search UI beneath,
                // with a Keep Current button to abort the swap.
                if (currentLinked && changingVoter) {
                  return (
                    <>
                      <div style={{
                        background:'var(--surface)', border:'1px dashed #38bdf8', borderRadius:6,
                        padding:8, display:'flex', justifyContent:'space-between', alignItems:'center', gap:8,
                        marginBottom:8
                      }}>
                        <div style={{minWidth:0, flex:1}}>
                          <div style={{fontSize:9, color:'var(--link)', marginBottom:2, textTransform:'uppercase', letterSpacing:0.5}}>
                            Current
                          </div>
                          <div style={{fontSize:11, color:'var(--text)'}}>
                            {currentLinked.name}
                            <span style={{color:'var(--text-3)', marginLeft:6, fontSize:9}}>
                              ({currentLinked.island}{currentLinked.ward ? ', ' + currentLinked.ward : ''})
                            </span>
                          </div>
                        </div>
                        <button
                          onClick={() => {
                            setChangingVoter(false);
                            setEditVoterSearch('');
                            setEditVoterResults([]);
                          }}
                          style={{
                            padding:'4px 10px', background:'transparent', border:'1px solid var(--text-3)',
                            borderRadius:4, color:'var(--text-2)', cursor:'pointer', fontSize:10, flexShrink:0
                          }}>
                          Keep Current
                        </button>
                      </div>
                      <input
                        value={editVoterSearch}
                        onChange={e => setEditVoterSearch(e.target.value)}
                        placeholder="Search for replacement voter..."
                        style={{...S.sel, marginBottom:6}}
                      />
                      <select
                        value=""
                        onChange={e => {
                          const selectedId = parseInt(e.target.value);
                          if (selectedId) {
                            // Stage the new voter. The green card will re-
                            // render with the new person on the next pass.
                            setEditingMember({...editingMember, voter_id: selectedId});
                            setEditVoterSearch('');
                            setEditVoterResults([]);
                            setChangingVoter(false);
                          }
                        }}
                        size={5}
                        style={{...S.sel, minHeight:100}}>
                        <option value="">— Pick a voter to swap in —</option>
                        {editVoterSearching && <option disabled>Searching...</option>}
                        {!editVoterSearching && editVoterSearch.trim() && editVoterResults.length === 0 && (
                          <option disabled>No voters found</option>
                        )}
                        {!editVoterSearching && editVoterResults
                          .filter(v => v.id !== editingMember.voter_id) // don't suggest the already-linked one
                          .map(v => (
                            <option key={v.id} value={v.id}>
                              {v.name} ({v.island}{v.ward ? ', ' + v.ward : ''})
                            </option>
                          ))}
                      </select>
                      <div style={{fontSize:9, color:'var(--text-3)', marginTop:4, fontStyle:'italic'}}>
                        Picking a voter replaces the current link. Name updates to match on Save.
                      </div>
                    </>
                  );
                }
                
                // State C: no voter linked — classic search UI (also used
                // immediately after Unlink until the user picks or saves).
                return (
                  <>
                    <input
                      value={editVoterSearch}
                      onChange={e => setEditVoterSearch(e.target.value)}
                      placeholder="Search voter by name..."
                      style={{...S.sel, marginBottom:6}}
                    />
                    <select
                      value=""
                      onChange={e => {
                        const selectedId = parseInt(e.target.value);
                        if (selectedId) {
                          setEditingMember({...editingMember, voter_id: selectedId});
                          setEditVoterSearch('');
                          setEditVoterResults([]);
                        }
                      }}
                      size={5}
                      style={{...S.sel, minHeight:100}}>
                      <option value="">— Manual entry (not in voter list) —</option>
                      {editVoterSearching && <option disabled>Searching...</option>}
                      {!editVoterSearching && editVoterSearch.trim() && editVoterResults.length === 0 && (
                        <option disabled>No voters found</option>
                      )}
                      {!editVoterSearching && editVoterResults.map(v => (
                        <option key={v.id} value={v.id}>
                          {v.name} ({v.island}{v.ward ? ', ' + v.ward : ''})
                        </option>
                      ))}
                    </select>
                    <div style={{fontSize:9, color:'var(--text-3)', marginTop:4, fontStyle:'italic'}}>
                      Manual entries cannot be navigated to from the family tree.
                    </div>
                  </>
                );
              })()}
            </MF>

            {/* Photo upload — available for both manual entries and voter-linked members.
                For linked members, the family-member photo (if set) overrides the
                voter's profile photo in the family tree view. Uploads go to Storage
                immediately; the DB column stores the path. */}
            <MF label={editingMember.voter_id ? 'Photo (optional — overrides voter\'s profile photo in tree)' : 'Photo (optional)'}>
              <EditMemberPhotoField
                supabase={supabase}
                editingMember={editingMember}
                setEditingMember={setEditingMember}
                linkedVoter={editingMember.voter_id
                  ? localVoters.find(v => String(v.id) === String(editingMember.voter_id)) || null
                  : null}
                refreshVoter={async () => {
                  // After removing the voter's photo, refresh localVoters so the preview updates
                  const data = await fetchAllVoters(supabase);
                  if (data) setLocalVoters(data);
                }}
              />
            </MF>

            <MF label="Spouse">
              <select
                value={editingMember.spouse_id || ''}
                onChange={e => setEditingMember({...editingMember, spouse_id: e.target.value || null})}
                style={S.sel}>
                <option value="">No spouse</option>
                {immediateMembers
                  .filter(m => m.id !== editingMember.id)
                  .map(m => (
                    <option key={m.id} value={m.id}>{m.name} ({m.role})</option>
                  ))}
              </select>
            </MF>

            <MF label="Parents (hold Ctrl/Cmd to select multiple, max 2)">
              <select
                multiple
                value={editingMember.parent_ids || []}
                onChange={e => {
                  const selected = Array.from(e.target.selectedOptions).map(opt => opt.value);
                  const prevParents = editingMember.parent_ids || [];
                  let next = selected;
                  if (selected.length > 2) {
                    // Cap at 2: keep whichever were already selected (in their
                    // existing order) plus the newly-added pick(s), then drop
                    // from the front — same "replace oldest" behavior used
                    // when auto-linking a 3rd parent elsewhere in this file.
                    const kept = prevParents.filter(id => selected.includes(id));
                    const added = selected.filter(id => !prevParents.includes(id));
                    next = [...kept, ...added].slice(-2);
                  }
                  setEditingMember({...editingMember, parent_ids: next});
                }}
                style={{...S.sel, height:120}}>
                {immediateMembers
                  .filter(m => m.id !== editingMember.id && !descendantIds.has(m.id))
                  .map(m => (
                    <option key={m.id} value={m.id}>{m.name} ({m.role})</option>
                  ))}
              </select>
            </MF>

            <div style={{display:'flex', gap:8, justifyContent:'flex-end', marginTop:16}}>
              <button 
                onClick={() => {setShowEditMember(false); setEditingMember(null); setChangingVoter(false);}}
                style={{
                  padding:'6px 14px', borderRadius:7, border:'1px solid var(--border)',
                  background:'transparent', color:'var(--text-2)', cursor:'pointer', fontSize:11
                }}>
                Cancel
              </button>
              <button 
                onClick={async () => {
                  // Capture old values BEFORE updating
                  const existing = treeFamily?.members.find(m => m.id === editingMember.id);
                  const oldSpouseId = existing?.spouse_id;
                  const oldVoterId = existing?.voter_id;
                  
                  // If a name was originally synced from a voter, refresh it from the
                  // newly-linked voter (so the tree shows the right name).
                  let memberName = editingMember.name;
                  if (editingMember.voter_id && editingMember.voter_id !== oldVoterId) {
                    const newLinked = localVoters.find(v => v.id === editingMember.voter_id);
                    if (newLinked) memberName = newLinked.name;
                  }
                  
                  // Update this member with new voter/spouse/parent/photo links.
                  // For voter-linked members the photo is written to voters.photo_url
                  // (so it shows on the voter profile too), and family_members.photo_url
                  // stays null — FamilyTree falls back to the voter's photo. For
                  // manual members photo_url stays on family_members.
                  const linkedVoterId = editingMember.voter_id || null;
                  const photoForVoter = linkedVoterId && editingMember.photo_url ? editingMember.photo_url : null;
                  const photoForMember = linkedVoterId ? null : (editingMember.photo_url || null);

                  if (photoForVoter) {
                    await supabase
                      .from('voters')
                      .update({ photo_url: photoForVoter })
                      .eq('id', linkedVoterId);
                    // If the linked voter is the one we're viewing, update the
                    // local form so the avatar refreshes without navigation.
                    if (linkedVoterId === voter.id) {
                      setForm(prev => ({ ...prev, photo_url: photoForVoter }));
                    }
                  }

                  const {error} = await supabase
                    .from('family_members')
                    .update({
                      voter_id: linkedVoterId,
                      name: memberName,
                      spouse_id: editingMember.spouse_id || null,
                      parent_ids: editingMember.parent_ids || [],
                      photo_url: photoForMember,
                    })
                    .eq('id', editingMember.id);

                  if (!error) {
                    // BIDIRECTIONAL SPOUSE LINKING
                    if (oldSpouseId && oldSpouseId !== editingMember.spouse_id) {
                      await supabase
                        .from('family_members')
                        .update({ spouse_id: null })
                        .eq('id', oldSpouseId);
                    }
                    if (editingMember.spouse_id) {
                      await supabase
                        .from('family_members')
                        .update({ spouse_id: editingMember.id })
                        .eq('id', editingMember.spouse_id);
                    }
                    
                    // VOTER LINK SYNC: keep voters.family_id in sync with family_members.voter_id
                    // so the link is bidirectional and the voter shows the same family.
                    if (oldVoterId && oldVoterId !== editingMember.voter_id) {
                      // Old voter is no longer linked — clear their family_id only if
                      // it pointed at this family (don't clobber unrelated families).
                      await supabase
                        .from('voters')
                        .update({ family_id: null })
                        .eq('id', oldVoterId)
                        .eq('family_id', myFamily?.id);
                    }
                    if (editingMember.voter_id && myFamily?.id) {
                      await supabase
                        .from('voters')
                        .update({ family_id: myFamily.id })
                        .eq('id', editingMember.voter_id);
                    }
                    
                    // Reload families (parent-side) and voters to reflect updates
                    if (refreshFamilies) await refreshFamilies();
                    const vData = await fetchAllVoters(supabase);
                    if (vData) setLocalVoters(vData);
                    
                    setShowEditMember(false);
                    setEditingMember(null);
                    setEditVoterSearch('');
                    setEditVoterResults([]);
                    setChangingVoter(false);
                  }
                }}
                style={{
                  padding:'6px 14px', borderRadius:7, border:'none',
                  background:'#10b981', color:'#fff', cursor:'pointer', fontSize:11, fontWeight:600
                }}>
                Save Links
              </button>
            </div>
          </Card>
        </div>
        );
      })()}

    </div>
  );
}

export default VoterProfile;

